import {
  Fragment,
  SetStateAction,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import { LuChevronDown } from "react-icons/lu";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { v4 as uuidv4 } from "uuid";
import { TextSelect, Trash2 } from "lucide-react";
import { useParams } from "react-router-dom";
import { fetchAssets, getUniqueLabels } from "@/api/globalApiService";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import Shimmer from "@/components/ui/shimmer";
import {
  ColumnOption,
  DataConfigurations,
  DataConfigurationsFilter,
  FilterOn,
  ReportDate,
  SectionFilter,
} from "@/types";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import DateRangePicker from "@/components/common/calendar/DateRangePicker";
import { TimeInterval } from "@/types";
import { useGlobalStore } from "@/GlobalStore";
import axios, { CancelTokenSource } from "axios";
import { TZDate } from "@date-fns/tz";
import { fromZonedTime } from "date-fns-tz";
import { cn } from "@/lib/utils";
import { AnimatePresence, motion } from "framer-motion";
import { IconDatabase, IconFilter } from "@tabler/icons-react";
import { getPlatformIcon, sortMetricItems } from "@/screens/utils/Helpers";
import {
  RiAddLargeLine,
  RiAddLine,
  RiArrowDownSLine,
  RiCloseLine,
  RiDeleteBackLine,
  RiFilter3Line,
  RiNodeTree,
  RiSearchLine,
  RiTimeLine,
} from "@remixicon/react";
import { SqButton } from "@/components/squircle-ui/button";
import MediaIcon from "@/components/common/MediaIcon";
import {
  SqTooltip,
  SqTooltipContent,
  SqTooltipProvider,
  SqTooltipTrigger,
} from "@/components/squircle-ui/tooltip";
import SelectDropdown from "@/components/common/dropdown/SelectDropdown";
import {
  SqCommand,
  SqCommandGroup,
  SqCommandInput,
  SqCommandItem,
} from "@/components/squircle-ui/command";
import {
  SqSelect,
  SqSelectContent,
  SqSelectItem,
  SqSelectTrigger,
} from "@/components/squircle-ui/select";
import { formatDateRange } from "@/utils/dateUtils";
import { searchSort } from "@/utils/searchSort";
import { SqInput } from "@/components/squircle-ui/input";
import {
  SqDropdownMenu,
  SqDropdownMenuContent,
  SqDropdownMenuGroup,
  SqDropdownMenuItem,
  SqDropdownMenuTrigger,
} from "@/components/squircle-ui/dropdown";

const FilterOnItems = [
  {
    key: FilterOn.ADS,
    value: FilterOn.ADS,
    label: "Ad filter",
  },
  {
    key: FilterOn.GROUPS,
    value: FilterOn.GROUPS,
    label: "Group filter",
  },
  {
    key: FilterOn.CREATIVES,
    value: FilterOn.CREATIVES,
    label: "Creative filter",
  },
];

// Repeated UI class names (DRY)
const SQUIRCLE_PANEL_BASE =
  "squircle squircle-smooth-[1] squircle-radius-[12px] rounded-[12px] squircle-outline-[1px] squircle-outline-color-border-gray bg-white drop-shadow-md";
const SQUIRCLE_PANEL_P1_160 = `${SQUIRCLE_PANEL_BASE} p-1 min-w-[160px]`;
const SQUIRCLE_PANEL_P1_180 = `${SQUIRCLE_PANEL_BASE} p-1 min-w-[180px]`;
const SQUIRCLE_PANEL_P6 = `${SQUIRCLE_PANEL_BASE} p-[6px]`;
const FILTER_PILL_BASE =
  "flex items-center h-7 relative squircle squircle-fill-[#FFF4F0] squircle-radius-[6px] squircle-smooth-[1] before:absolute before:inset-0 before:squircle before:squircle-radius-[6px] before:squircle-smooth-[1] before:squircle-outline-[1px] before:squircle-outline-color-[#FFDACC] before:pointer-events-none";
const MENU_ITEM_CLS =
  "cursor-pointer px-[8px] py-[4px] rounded-[8px] hover:bg-hover-gray";
const MENU_TEXT_CLS = "text-brand-primary-new/80 text-[12px] font-normal leading-[20px]";
const NUMERIC_INPUT_CLS =
  "flex w-[180px] items-center gap-[6px] rounded-[8px] overflow-hidden text-[#35467A] text-[12px] font-medium leading-[19.157px] tracking-[0.24px] placeholder:text-[#02185966] placeholder:font-normal px-[8px]";
const OP_BTN_CLS =
  "group/op h-7 px-2 py-0 pb-[2px] border-l border-brand-orange/20 bg-transparent hover:bg-[#FCE5D9] transition-colors cursor-pointer focus:outline-none focus:ring-0";
const OP_LABEL_CLS =
  "text-[#FF8F66] group-hover/op:text-brand-orange transition-colors text-[11px] font-normal leading-[19.157px] tracking-[0.24px] lowercase whitespace-nowrap";
const CLOSE_BTN_CLS =
  "group/close border-l border-brand-orange/20 hover:bg-[#FCE5D9] transition-colors px-1.5 flex items-center justify-center h-7 cursor-pointer rounded-r-[6px]";
const CLOSE_ICON_CLS =
  "text-brand-orange/40 group-hover/close:text-brand-orange transition-colors";
const PILL_LABEL_CLS =
  "pl-3 pr-2 text-brand-orange text-[11px] font-normal leading-[19.157px] tracking-[0.24px] whitespace-nowrap";
const OR_LABEL_CLS =
  "text-[#67749B] text-[11px] font-normal leading-[19.157px] tracking-[0.24px] w-5 shrink-0";

const FILTER_PILL_MOTION = {
  layout: true as const,
  initial: { opacity: 0, scale: 0.85 },
  animate: { opacity: 1, scale: 1 },
  exit: { opacity: 0, scale: 0.85 },
  transition: {
    layout: { type: "spring" as const, stiffness: 500, damping: 35 },
    opacity: { duration: 0.15 },
    scale: { duration: 0.2 },
  },
};

const FILTER_ROW_MOTION = {
  layout: true as const,
  initial: { opacity: 0, y: -6 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -6 },
  transition: {
    layout: { type: "spring" as const, stiffness: 500, damping: 35 },
    opacity: { duration: 0.15 },
    y: { type: "spring" as const, stiffness: 400, damping: 28 },
  },
};

const OperatorBlock = ({
  operators,
  operatorLabel,
  conditionFilterType,
  currentOperator,
  onSelectOperator,
  disabled,
}: {
  operators: { key: string; label: string }[] | undefined;
  operatorLabel: string;
  conditionFilterType: string | undefined;
  currentOperator: string;
  onSelectOperator: (key: string) => void;
  disabled?: boolean;
}) => {
  if (disabled || !operators || operators.length <= 1) {
    return (
      <span className="h-7 px-2 py-0 pb-[2px] border-l border-brand-orange/20 inline-flex items-center text-[#FF8F66] text-[11px] font-normal lowercase whitespace-nowrap">
        {operatorLabel}
      </span>
    );
  }

  if (operators.length === 2 && !METRIC_SIDE_PANEL_TYPES.includes(conditionFilterType || "")) {
    return (
      <button
        className={OP_BTN_CLS}
        onClick={() => {
          const other = operators.find((o) => o.key !== currentOperator) ?? operators[0];
          onSelectOperator(other.key);
        }}
      >
        <span className={OP_LABEL_CLS}>{operatorLabel}</span>
      </button>
    );
  }

  return (
    <SqDropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className={OP_BTN_CLS}>
          <span className={OP_LABEL_CLS}>{operatorLabel}</span>
        </button>
      </DropdownMenuTrigger>
      <SqDropdownMenuContent align="start" radius={12} className="rounded-[8px]">
        {operators && METRIC_SIDE_PANEL_TYPES.includes(conditionFilterType || "") ? (
          <GroupedOperatorDropdownContent
            operators={operators}
            selectedOperatorKey={currentOperator || ""}
            onSelectOperator={onSelectOperator}
          />
        ) : (
          <SqDropdownMenuGroup>
            {operators?.map((operator) => (
              <SqDropdownMenuItem
                key={operator.key}
                isSelected={operator.key === currentOperator}
                onClick={() => onSelectOperator(operator.key)}
                className="cursor-pointer"
              >
                <span className={MENU_TEXT_CLS}>{operator.label}</span>
              </SqDropdownMenuItem>
            ))}
          </SqDropdownMenuGroup>
        )}
      </SqDropdownMenuContent>
    </SqDropdownMenu>
  );
};

const PillCloseButton = ({
  onClick,
  dataAdvancedDismiss,
}: {
  onClick: () => void;
  dataAdvancedDismiss?: boolean;
}) => (
  <button
    {...(dataAdvancedDismiss ? { "data-advanced-dismiss": true } : {})}
    onClick={onClick}
    className={CLOSE_BTN_CLS}
  >
    <RiCloseLine className={CLOSE_ICON_CLS} size={16} />
  </button>
);

const PlatformIcons = ({ platforms }: { platforms: string[] }) => (
  <div className="flex items-center gap-1">
    {platforms.length <= 2 ? (
      platforms.map((p, index) => (
        <img
          key={index}
          className="h-[12px] w-[12px] flex-shrink-0"
          src={getPlatformIcon(p as Parameters<typeof getPlatformIcon>[0])}
        />
      ))
    ) : (
      <>
        <img
          className="h-[12px] w-[12px] flex-shrink-0"
          src={getPlatformIcon(platforms[0] as Parameters<typeof getPlatformIcon>[0])}
        />
        <span className="translate-y-[1px] text-[#02185999]">
          +{platforms.length - 1}
        </span>
      </>
    )}
  </div>
);

const PlatformAvailabilityText = ({ platforms }: { platforms: string[] }) => (
  <p>
    Available for{" "}
    {platforms
      .map((p) => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase())
      .join(", ")}
  </p>
);

const NumericValueInput = ({
  label = "Value",
  value,
  onChange,
  onCommit,
  placeholder = "Enter value",
  prefix,
  suffix,
}: {
  label?: string;
  value: string;
  onChange: (v: string) => void;
  onCommit: () => void;
  placeholder?: string;
  prefix?: string;
  suffix?: string;
}) => (
  <div className={SQUIRCLE_PANEL_P6}>
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <span className="text-[12px] font-medium text-[#35467A] pl-[4px]">{label}</span>
        <SqButton
          variant="default"
          size="sm"
          className="text-white border-0 cursor-pointer squircle-mask"
          onClick={onCommit}
        >
          Apply
        </SqButton>
      </div>
      <div className="relative flex items-center">
        {prefix && value !== "" && (
          <span className="absolute left-[10px] text-[12px] text-[#02185966] pointer-events-none">{prefix}</span>
        )}
        <SqInput
          shadow="sm"
          autoFocus
          value={value}
          onChange={(e) => {
            const val = e.target.value;
            if (val === "" || /^\d*\.?\d*$/.test(val)) onChange(val);
          }}
          onKeyDown={(e) => e.key === "Enter" && onCommit()}
          className={cn(NUMERIC_INPUT_CLS, prefix && value !== "" && "pl-[22px]")}
          type="text"
          inputMode="decimal"
          placeholder={placeholder}
        />
        {suffix && value !== "" && (
          <span className={cn("absolute flex items-center pointer-events-none", prefix ? "left-[22px]" : "left-[10px]")}>
            <span className="text-[12px] font-medium invisible whitespace-pre">{value}</span>
            <span className="text-[12px] text-[#02185966] ml-[2px]">{suffix}</span>
          </span>
        )}
      </div>
    </div>
  </div>
);

const BetweenValueInput = ({
  label = "Min / Max",
  minValue,
  maxValue,
  onMinChange,
  onMaxChange,
  onCommit,
  prefix,
  suffix,
}: {
  label?: string;
  minValue: string;
  maxValue: string;
  onMinChange: (v: string) => void;
  onMaxChange: (v: string) => void;
  onCommit: () => void;
  prefix?: string;
  suffix?: string;
}) => (
  <div className={SQUIRCLE_PANEL_P6}>
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <span className="text-[12px] font-medium text-[#35467A] pl-[4px]">{label}</span>
        <SqButton
          variant="default"
          size="sm"
          className="text-white border-0 cursor-pointer squircle-mask"
          onClick={onCommit}
        >
          Apply
        </SqButton>
      </div>
      <div className="flex gap-2">
        <div className="relative flex items-center">
          {prefix && minValue !== "" && (
            <span className="absolute left-[8px] text-[12px] text-[#02185966] pointer-events-none">{prefix}</span>
          )}
          <SqInput
            shadow="sm"
            autoFocus
            value={minValue}
            onChange={(e) => {
              const val = e.target.value;
              if (val === "" || /^\d*\.?\d*$/.test(val)) onMinChange(val);
            }}
            onKeyDown={(e) => e.key === "Enter" && onCommit()}
            className={cn(
              "flex w-[90px] items-center gap-[6px] rounded-[8px] overflow-hidden text-[#35467A] text-[12px] font-medium leading-[19.157px] placeholder:text-[#02185966] placeholder:font-normal px-[8px]",
              prefix && minValue !== "" && "pl-[20px]",
            )}
            type="text"
            inputMode="decimal"
            placeholder="Min"
          />
          {suffix && minValue !== "" && (
            <span className={cn("absolute flex items-center pointer-events-none", prefix ? "left-[20px]" : "left-[8px]")}>
              <span className="text-[12px] font-medium invisible whitespace-pre">{minValue}</span>
              <span className="text-[12px] text-[#02185966] ml-[2px]">{suffix}</span>
            </span>
          )}
        </div>
        <div className="relative flex items-center">
          {prefix && maxValue !== "" && (
            <span className="absolute left-[8px] text-[12px] text-[#02185966] pointer-events-none">{prefix}</span>
          )}
          <SqInput
            shadow="sm"
            value={maxValue}
            onChange={(e) => {
              const val = e.target.value;
              if (val === "" || /^\d*\.?\d*$/.test(val)) onMaxChange(val);
            }}
            onKeyDown={(e) => e.key === "Enter" && onCommit()}
            className={cn(
              "flex w-[90px] items-center gap-[6px] rounded-[8px] overflow-hidden text-[#35467A] text-[12px] font-medium leading-[19.157px] placeholder:text-[#02185966] placeholder:font-normal px-[8px]",
              prefix && maxValue !== "" && "pl-[20px]",
            )}
            type="text"
            inputMode="decimal"
            placeholder="Max"
          />
          {suffix && maxValue !== "" && (
            <span className={cn("absolute flex items-center pointer-events-none", prefix ? "left-[20px]" : "left-[8px]")}>
              <span className="text-[12px] font-medium invisible whitespace-pre">{maxValue}</span>
              <span className="text-[12px] text-[#02185966] ml-[2px]">{suffix}</span>
            </span>
          )}
        </div>
      </div>
    </div>
  </div>
);

export type Props = {
  selectedFilters: SectionFilter[] | null;
  onFilterChange: (filters: SectionFilter[] | null) => void;
  disabled?: boolean;
  showAddFilters?: boolean;
  selectedDate: ReportDate | null;
  dataConfigFilter: DataConfigurationsFilter;
  showSelectedFilters?: boolean;
  customTrigger?: React.ReactNode;
  clearSelection?: boolean;
  selectedFilterSize?: number;
  className?: string;
  showAdvancedFilter?: boolean;
  onShowAdvancedFilterChange?: (show: boolean) => void;
  advancedTempFilter?: SectionFilter;
  onAdvancedTempFilterChange?: (filter: SectionFilter | undefined) => void;
  advancedOpenedAtRef?: React.MutableRefObject<number>;
  columnChoices?: DataConfigurations["column_choices"];
  hideAdvancedFilters?: boolean;
};

const Filters = (props: Props) => {
  const {
    selectedFilters,
    onFilterChange,
    disabled = false,
    showAddFilters = true,
    selectedDate,
    dataConfigFilter,
    showSelectedFilters = true,
    customTrigger,
    clearSelection,
    selectedFilterSize,
    className,
    showAdvancedFilter,
    onShowAdvancedFilterChange,
    advancedTempFilter,
    onAdvancedTempFilterChange,
    advancedOpenedAtRef: advancedOpenedAtRefProp,
    columnChoices,
    hideAdvancedFilters,
  } = props;
  const selectedAccount = useGlobalStore((state) => state.selectedAccount);
  const [openFilterId, setOpenFilterId] = useState<string | null>(null);
  const [advancedFilterDropdownOpen, setAdvancedFilterDropdownOpen] = useState(false);
  const [editingAdvancedFilterId, setEditingAdvancedFilterId] = useState<string | null>(
    null,
  );
  const localAdvancedOpenedAtRef = useRef<number>(0);
  const advancedOpenedAtRef = advancedOpenedAtRefProp ?? localAdvancedOpenedAtRef;

  const selectedFiltersRef = useRef(selectedFilters);
  selectedFiltersRef.current = selectedFilters;

  const columnOptionMap = useMemo(() => {
    if (!columnChoices) return null;
    const map = new Map<string, ColumnOption>();
    for (const group of columnChoices) {
      for (const opt of group.options) {
        map.set(opt.key, opt);
      }
    }
    return map;
  }, [columnChoices]);

  // Helper function to set filter_on based on available options
  const updateTempFilterWithFilterOn = (newTempFilter: SectionFilter | undefined) => {
    if (!newTempFilter) return newTempFilter;

    // Calculate available options for the new conditions
    const newAvailableOptions =
      newTempFilter.conditions
        ?.map((condition) => {
          const filterChoice = dataConfigFilter?.filter_choices.find(
            (choice) => choice.key === condition.parent_key,
          );
          return filterChoice?.filter_on || [];
        })
        .filter((options) => options.length > 0) || [];

    //  there shouldn't be filter whose filter_on is empty if there return without filter_on
    if (newAvailableOptions.length === 0) return newTempFilter;

    // Find intersection
    const intersection = newAvailableOptions.reduce((intersection, current) => {
      return intersection.filter((option) => current.includes(option));
    }, newAvailableOptions[0] || []);

    // Keep existing filter_on if it's still valid, otherwise use first available option
    const currentFilterOn = newTempFilter.filter_on;
    const isCurrentFilterOnValid =
      currentFilterOn && intersection.includes(currentFilterOn);

    return {
      ...newTempFilter,
      filter_on: isCurrentFilterOnValid
        ? currentFilterOn
        : intersection[0] || FilterOn.ADS,
    };
  };

  const handleRemoveFilter = (filter: SectionFilter) => {
    if (!selectedFilters) return;
    onFilterChange(selectedFilters?.filter((f) => f.id !== filter.id) || []);
  };

  const handleAddFilterKeySelect = useCallback(
    (
      item: NonNullable<DataConfigurationsFilter["filter_choices"]>[number],
      child?: NonNullable<(typeof item)["children"]>[number],
    ) => {
      const selectedFilter = child || item;
      const firstOperator = dataConfigFilter?.filter_types.find(
        (type) => type.type === selectedFilter.filter_type,
      )?.operators[0];

      const newFilter: SectionFilter = {
        id: uuidv4(),
        key: selectedFilter.key,
        field: "field" in selectedFilter ? selectedFilter.field : "",
        label: selectedFilter.label,
        filter_type: selectedFilter.filter_type,
        sub_type: child?.sub_type,
        type: selectedFilter?.type,
        conditions: [
          {
            operator: firstOperator?.key ?? "",
            value: getInitialValueForFilterType(
              selectedFilter.filter_type,
              selectedFilter?.type,
            ),
            label: selectedFilter.label,
            key: selectedFilter.key,
            parent_key: child ? item.key : selectedFilter.key,
          },
        ],
      };
      const withFilterOn = updateTempFilterWithFilterOn(newFilter);
      const finalFilter = withFilterOn ?? newFilter;

      onFilterChange(selectedFilters ? [...selectedFilters, finalFilter] : [finalFilter]);
      setOpenFilterId(null);
    },
    [dataConfigFilter, selectedFilters, onFilterChange, updateTempFilterWithFilterOn],
  );

  const handleAssetFilterCommit = useCallback(
    (
      item: NonNullable<DataConfigurationsFilter["filter_choices"]>[number],
      value: string | string[],
      parentKey?: string,
      operatorKey?: string,
      existingFilterId?: string,
    ): string | undefined => {
      const isAssetFilter = ASSET_FILTER_KEYS.includes(item.key);
      const resolvedOperator = isAssetFilter
        ? "CONTAIN_ANY"
        : operatorKey
          ? operatorKey
          : (dataConfigFilter?.filter_types.find((type) => type.type === item.filter_type)
              ?.operators[0]?.key ?? "");

      if (isAssetFilter) {
        const newValues = Array.isArray(value) ? value : [value];
        const latestFilters = selectedFiltersRef.current;

        const existingFilter = existingFilterId
          ? latestFilters?.find((f) => f.id === existingFilterId)
          : undefined;

        let nextFilters: SectionFilter[];
        let filterId: string;

        if (existingFilter) {
          const prev = existingFilter.conditions?.[0]?.value;
          const prevValues = Array.isArray(prev)
            ? (prev as string[])
            : prev
              ? [prev as string]
              : [];
          const allValues = [...new Set([...prevValues, ...newValues])];
          const useArray = allValues.length > 1;
          filterId = existingFilter.id!;
          nextFilters =
            latestFilters?.map((f) =>
              f.id === existingFilter.id
                ? {
                    ...f,
                    conditions: [
                      {
                        ...f.conditions![0],
                        operator: resolveAssetOp(f.conditions![0].operator, allValues.length),
                        value: useArray ? allValues : allValues[0],
                      },
                    ],
                  }
                : f,
            ) || [];
        } else {
          const isSingle = newValues.length === 1;
          filterId = uuidv4();
          const newFilter: SectionFilter = {
            id: filterId,
            key: item.key,
            field: "field" in item ? item.field : "",
            label: item.label,
            filter_type: item.filter_type,
            sub_type:
              "sub_type" in item ? (item as { sub_type?: string }).sub_type : undefined,
            type: item?.type,
            conditions: [
              {
                operator: isSingle ? "CONTAIN" : "CONTAIN_ANY",
                value: isSingle ? newValues[0] : newValues,
                label: item.label,
                key: item.key,
                parent_key: parentKey || item.key,
              },
            ],
          };
          const withFilterOn = updateTempFilterWithFilterOn(newFilter);
          const finalFilter = withFilterOn ?? newFilter;
          nextFilters = latestFilters ? [...latestFilters, finalFilter] : [finalFilter];
        }
        selectedFiltersRef.current = nextFilters;
        onFilterChange(nextFilters);
        return filterId;
      }

      const newFilter: SectionFilter = {
        id: uuidv4(),
        key: item.key,
        field: "field" in item ? item.field : "",
        label: item.label,
        filter_type: item.filter_type,
        sub_type:
          "sub_type" in item ? (item as { sub_type?: string }).sub_type : undefined,
        type: item?.type,
        conditions: [
          {
            operator: resolvedOperator,
            value,
            label: item.label,
            key: item.key,
            parent_key: parentKey || item.key,
          },
        ],
      };
      const withFilterOn = updateTempFilterWithFilterOn(newFilter);
      const finalFilter = withFilterOn ?? newFilter;

      onFilterChange(selectedFilters ? [...selectedFilters, finalFilter] : [finalFilter]);
      setOpenFilterId(null);
    },
    [dataConfigFilter, selectedFilters, onFilterChange, updateTempFilterWithFilterOn],
  );

  const handleAssetFilterRemoveValue = useCallback(
    (
      item: NonNullable<DataConfigurationsFilter["filter_choices"]>[number],
      valueToRemove: string,
    ) => {
      const latestFilters = selectedFiltersRef.current;
      const existing = latestFilters?.find(
        (f) =>
          f.key === item.key &&
          ALL_ASSET_OPS.includes(f.conditions?.[0]?.operator ?? ""),
      );
      if (!existing) return;

      const prev = existing.conditions?.[0]?.value;
      const prevValues = Array.isArray(prev)
        ? (prev as string[])
        : prev
          ? [prev as string]
          : [];
      const remaining = prevValues.filter((v) => v !== valueToRemove);

      let nextFilters: SectionFilter[];
      if (remaining.length === 0) {
        nextFilters = latestFilters?.filter((f) => f.id !== existing.id) || [];
      } else {
        const useSingle = remaining.length === 1;
        nextFilters =
          latestFilters?.map((f) =>
            f.id === existing.id
              ? {
                  ...f,
                  conditions: [
                    {
                      ...f.conditions![0],
                      operator: resolveAssetOp(f.conditions![0].operator, remaining.length),
                      value: useSingle ? remaining[0] : remaining,
                    },
                  ],
                }
              : f,
          ) || [];
      }
      selectedFiltersRef.current = nextFilters;
      onFilterChange(nextFilters);
    },
    [onFilterChange],
  );

  const updateFilterOperator = useCallback(
    (filterId: string | undefined, newOperator: string) => {
      if (!selectedFilters || !filterId) return;
      onFilterChange(
        selectedFilters.map((f) => {
          if (f.id !== filterId) return f;
          const conditions = [...(f.conditions || [])];
          if (conditions[0]) {
            let updatedValue = conditions[0].value;

            if (
              f.filter_type === "date" &&
              conditions[0].value &&
              typeof conditions[0].value === "object" &&
              "start_date" in conditions[0].value
            ) {
              const dateValue = conditions[0].value as {
                start_date: string;
                end_date: string;
                label?: TimeInterval;
              };
              const isSwitchingToSingle =
                newOperator === "DATE_ON_OR_AFTER" || newOperator === "DATE_ON_OR_BEFORE";
              const isSwitchingToBetween = newOperator === "DATE_BETWEEN";

              if (isSwitchingToSingle) {
                updatedValue = {
                  start_date: dateValue.start_date,
                  end_date: dateValue.start_date,
                  label: dateValue.label ?? TimeInterval.CUSTOM,
                };
              } else if (
                isSwitchingToBetween &&
                dateValue.start_date === dateValue.end_date
              ) {
                updatedValue = {
                  start_date: dateValue.start_date,
                  end_date: dateValue.start_date,
                  label: dateValue.label ?? TimeInterval.CUSTOM,
                };
              }
            }

            conditions[0] = {
              ...conditions[0],
              operator: newOperator,
              value: updatedValue,
            };
          }
          return { ...f, conditions };
        }),
      );
    },
    [selectedFilters, onFilterChange],
  );

  const updateFilterValue = useCallback(
    (
      filterId: string | undefined,
      newValue:
        | string
        | string[]
        | number
        | { min: number; max: number }
        | { start_date: string; end_date: string; label: TimeInterval },
    ) => {
      if (!selectedFilters || !filterId) return;
      onFilterChange(
        selectedFilters.map((f) => {
          if (f.id !== filterId) return f;
          const conditions = [...(f.conditions || [])];
          if (conditions[0]) {
            conditions[0] = { ...conditions[0], value: newValue };
          }
          return { ...f, conditions };
        }),
      );
    },
    [selectedFilters, onFilterChange],
  );

  const updateFilterValueAndOperator = useCallback(
    (filterId: string | undefined, newValue: string | string[], newOperator: string) => {
      if (!selectedFilters || !filterId) return;
      onFilterChange(
        selectedFilters.map((f) => {
          if (f.id !== filterId) return f;
          const conditions = [...(f.conditions || [])];
          if (conditions[0]) {
            conditions[0] = { ...conditions[0], value: newValue, operator: newOperator };
          }
          return { ...f, conditions };
        }),
      );
    },
    [selectedFilters, onFilterChange],
  );

  const updateFilterOn = useCallback(
    (filterId: string | undefined, newFilterOn: FilterOn) => {
      if (!selectedFilters || !filterId) return;
      onFilterChange(
        selectedFilters.map((f) =>
          f.id === filterId ? { ...f, filter_on: newFilterOn } : f,
        ),
      );
    },
    [selectedFilters, onFilterChange],
  );

  const handleAdvancedAddFilterKeySelect = useCallback(
    (
      item: NonNullable<DataConfigurationsFilter["filter_choices"]>[number],
      child?: NonNullable<(typeof item)["children"]>[number],
    ) => {
      const selectedFilter = child || item;
      const firstOperator = dataConfigFilter?.filter_types.find(
        (type) => type.type === selectedFilter.filter_type,
      )?.operators[0];

      const newCondition = {
        operator: firstOperator?.key ?? "",
        value: getInitialValueForFilterType(
          selectedFilter.filter_type,
          selectedFilter?.type,
        ),
        label: selectedFilter.label,
        key: selectedFilter.key,
        parent_key: child ? item.key : selectedFilter.key,
      };

      if (!advancedTempFilter) {
        const newFilter: SectionFilter = {
          id: uuidv4(),
          key: selectedFilter.key,
          field: "field" in selectedFilter ? selectedFilter.field : "",
          label: selectedFilter.label,
          filter_type: selectedFilter.filter_type,
          sub_type: child?.sub_type,
          type: selectedFilter?.type,
          conditions: [newCondition],
        };
        const withFilterOn = updateTempFilterWithFilterOn(newFilter);
        onAdvancedTempFilterChange?.(withFilterOn ?? newFilter);
      } else {
        const updatedFilter = {
          ...advancedTempFilter,
          conditions: [...(advancedTempFilter.conditions || []), newCondition],
        };
        const withFilterOn = updateTempFilterWithFilterOn(updatedFilter);
        onAdvancedTempFilterChange?.(withFilterOn ?? updatedFilter);
      }
      setAdvancedFilterDropdownOpen(false);
    },
    [
      dataConfigFilter,
      advancedTempFilter,
      onAdvancedTempFilterChange,
      updateTempFilterWithFilterOn,
    ],
  );

  const handleAdvancedRemoveCondition = useCallback(
    (conditionIndex: number) => {
      if (!advancedTempFilter?.conditions) return;
      const newConditions = advancedTempFilter.conditions.filter(
        (_, i) => i !== conditionIndex,
      );
      if (newConditions.length === 0) {
        onAdvancedTempFilterChange?.(undefined);
      } else {
        const updatedFilter = { ...advancedTempFilter, conditions: newConditions };
        const withFilterOn = updateTempFilterWithFilterOn(updatedFilter);
        onAdvancedTempFilterChange?.(withFilterOn ?? updatedFilter);
      }
    },
    [advancedTempFilter, onAdvancedTempFilterChange, updateTempFilterWithFilterOn],
  );

  const handleAdvancedUpdateOperator = useCallback(
    (conditionIndex: number, newOperator: string) => {
      if (!advancedTempFilter?.conditions) return;
      const newConditions = [...advancedTempFilter.conditions];
      if (newConditions[conditionIndex]) {
        let updatedValue = newConditions[conditionIndex].value;
        if (
          advancedTempFilter.filter_type === "date" &&
          newConditions[conditionIndex].value &&
          typeof newConditions[conditionIndex].value === "object" &&
          "start_date" in (newConditions[conditionIndex].value as object)
        ) {
          const dateValue = newConditions[conditionIndex].value as {
            start_date: string;
            end_date: string;
            label?: TimeInterval;
          };
          const isSwitchingToSingle =
            newOperator === "DATE_ON_OR_AFTER" || newOperator === "DATE_ON_OR_BEFORE";
          if (isSwitchingToSingle) {
            updatedValue = {
              start_date: dateValue.start_date,
              end_date: dateValue.start_date,
              label: dateValue.label ?? TimeInterval.CUSTOM,
            };
          }
        }
        newConditions[conditionIndex] = {
          ...newConditions[conditionIndex],
          operator: newOperator,
          value: updatedValue,
        };
      }
      onAdvancedTempFilterChange?.({ ...advancedTempFilter, conditions: newConditions });
    },
    [advancedTempFilter, onAdvancedTempFilterChange],
  );

  const handleAdvancedUpdateValue = useCallback(
    (
      conditionIndex: number,
      newValue:
        | string
        | string[]
        | number
        | { min: number; max: number }
        | { start_date: string; end_date: string; label: TimeInterval },
    ) => {
      if (!advancedTempFilter?.conditions) return;
      const newConditions = [...advancedTempFilter.conditions];
      if (newConditions[conditionIndex]) {
        newConditions[conditionIndex] = {
          ...newConditions[conditionIndex],
          value: newValue,
        };
      }
      onAdvancedTempFilterChange?.({ ...advancedTempFilter, conditions: newConditions });
    },
    [advancedTempFilter, onAdvancedTempFilterChange],
  );

  const handleAdvancedUpdateValueAndOperator = useCallback(
    (conditionIndex: number, newValue: string | string[], newOperator: string) => {
      if (!advancedTempFilter?.conditions) return;
      const newConditions = [...advancedTempFilter.conditions];
      if (newConditions[conditionIndex]) {
        newConditions[conditionIndex] = {
          ...newConditions[conditionIndex],
          value: newValue,
          operator: newOperator,
        };
      }
      onAdvancedTempFilterChange?.({ ...advancedTempFilter, conditions: newConditions });
    },
    [advancedTempFilter, onAdvancedTempFilterChange],
  );

  const commitAdvancedFilter = useCallback(() => {
    if (!advancedTempFilter?.conditions) return;
    const validConditions = advancedTempFilter.conditions.filter(validateCondition);
    if (validConditions.length > 0) {
      const finalFilter = { ...advancedTempFilter, conditions: validConditions };
      if (editingAdvancedFilterId) {
        onFilterChange(
          selectedFilters?.map((f) =>
            f.id === editingAdvancedFilterId ? finalFilter : f,
          ) ?? [finalFilter],
        );
      } else {
        onFilterChange(
          selectedFilters ? [...selectedFilters, finalFilter] : [finalFilter],
        );
      }
    }
    onAdvancedTempFilterChange?.(undefined);
    onShowAdvancedFilterChange?.(false);
    setEditingAdvancedFilterId(null);
  }, [
    advancedTempFilter,
    editingAdvancedFilterId,
    selectedFilters,
    onFilterChange,
    onAdvancedTempFilterChange,
    onShowAdvancedFilterChange,
  ]);

  const dismissAdvancedFilter = useCallback(() => {
    onAdvancedTempFilterChange?.(undefined);
    onShowAdvancedFilterChange?.(false);
    setEditingAdvancedFilterId(null);
  }, [onAdvancedTempFilterChange, onShowAdvancedFilterChange]);

  return (
    <div className={cn("flex flex-wrap gap-2", className)}>
      {/* Filters */}
      <AnimatePresence mode="popLayout" initial={false}>
        {showSelectedFilters &&
          selectedFilters?.map((filter) => {
            const condition = filter.conditions?.[0];
            if (!condition) return null;
            const isMultiCondition = (filter.conditions?.length || 0) > 1;

            if (isMultiCondition) {
              const summaryText = getFilterTooltipText(
                filter,
                dataConfigFilter,
                selectedAccount || null,
                columnOptionMap,
              );
              const isEditing =
                editingAdvancedFilterId === filter.id && showAdvancedFilter;
              const pill = (
                <div
                  className={cn(
                    FILTER_PILL_BASE,
                    selectedFilterSize && `h-[${selectedFilterSize}px]`,
                  )}
                >
                  {(() => {
                    const { content, overflow } = getFilterSummaryRichText(
                      filter,
                      dataConfigFilter,
                      selectedAccount || null,
                      2,
                      columnOptionMap,
                    );
                    return (
                      <SqTooltipProvider delayDuration={500}>
                        <SqTooltip>
                          <SqTooltipTrigger asChild>
                            <span
                              className={cn(
                                "flex items-center pl-3 pr-2 text-brand-orange text-[11px] font-normal leading-[19.157px] tracking-[0.24px] whitespace-nowrap cursor-pointer hover:bg-[#FCE5D9] active:bg-[#FCE5D9] transition-colors h-7 rounded-l-[6px]",
                                isEditing && "bg-[#FCE5D9]",
                              )}
                              onClick={() => {
                                if (disabled || isEditing) return;
                                advancedOpenedAtRef.current = Date.now();
                                onAdvancedTempFilterChange?.({ ...filter });
                                onShowAdvancedFilterChange?.(true);
                                setEditingAdvancedFilterId(filter.id ?? null);
                              }}
                            >
                              <span className="truncate max-w-[70ch]">{content}</span>
                              {overflow > 0 && (
                                <span className="shrink-0 text-[#FF8F66] font-medium ml-1">
                                  +{overflow} more
                                </span>
                              )}
                            </span>
                          </SqTooltipTrigger>
                          <SqTooltipContent
                            portal={true}
                            side="top"
                            align="start"
                            className="max-w-[400px] z-50 whitespace-normal break-words"
                          >
                            {summaryText}
                          </SqTooltipContent>
                        </SqTooltip>
                      </SqTooltipProvider>
                    );
                  })()}
                  {!disabled && (
                    <button
                      data-advanced-dismiss
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveFilter(filter);
                        if (isEditing) dismissAdvancedFilter();
                      }}
                      className={CLOSE_BTN_CLS}
                    >
                      <RiCloseLine className={CLOSE_ICON_CLS} size={16} />
                    </button>
                  )}
                </div>
              );

              if (isEditing) {
                return (
                  <motion.div key={filter.id} {...FILTER_PILL_MOTION}>
                    <Popover open modal={false}>
                      <PopoverTrigger asChild>{pill}</PopoverTrigger>
                      <AdvancedFilterPopoverContent
                        advancedTempFilter={advancedTempFilter}
                        dataConfigFilter={dataConfigFilter}
                        selectedDate={selectedDate}
                        selectedAccount={selectedAccount || null}
                        advancedFilterDropdownOpen={advancedFilterDropdownOpen}
                        setAdvancedFilterDropdownOpen={setAdvancedFilterDropdownOpen}
                        handleAdvancedUpdateOperator={handleAdvancedUpdateOperator}
                        handleAdvancedUpdateValue={handleAdvancedUpdateValue}
                        handleAdvancedUpdateValueAndOperator={handleAdvancedUpdateValueAndOperator}
                        handleAdvancedRemoveCondition={handleAdvancedRemoveCondition}
                        handleAdvancedAddFilterKeySelect={
                          handleAdvancedAddFilterKeySelect
                        }
                        onAdvancedTempFilterChange={onAdvancedTempFilterChange}
                        updateTempFilterWithFilterOn={updateTempFilterWithFilterOn}
                        commitAdvancedFilter={commitAdvancedFilter}
                        dismissAdvancedFilter={dismissAdvancedFilter}
                        editingFilterId={editingAdvancedFilterId}
                        selectedFilters={selectedFilters}
                        advancedOpenedAtRef={advancedOpenedAtRef}
                        columnOptionMap={columnOptionMap}
                      />
                    </Popover>
                  </motion.div>
                );
              }
              return (
                <motion.div key={filter.id} {...FILTER_PILL_MOTION}>
                  {pill}
                </motion.div>
              );
            }

            const conditionFilterType = resolveConditionFilterType(condition, filter, dataConfigFilter);

            const { operators, operatorLabel } = getFilteredOperators(
              conditionFilterType, condition.key, filter.key,
              condition.value, condition.operator, dataConfigFilter,
            );

            // Only show filter_on for metric filters
            const isMetricFilter = condition.parent_key === "metric";
            const filterChoice = isMetricFilter
              ? dataConfigFilter?.filter_choices.find(
                  (choice) => choice.key === (condition.parent_key || condition.key),
                )
              : undefined;
            const availableFilterOnOptions = filterChoice?.filter_on || [];
            const hasMultipleFilterOnOptions = availableFilterOnOptions.length > 1;
            const currentFilterOnLabel = FilterOnItems.find(
              (f) => f.value === filter.filter_on,
            )?.label;

            return (
              <motion.div
                key={filter.id}
                {...FILTER_PILL_MOTION}
                className={cn(
                  FILTER_PILL_BASE,
                  selectedFilterSize && `h-[${selectedFilterSize}px]`,
                )}
              >
                {/* Block 0: Filter On - only for metric filters */}
                {isMetricFilter && availableFilterOnOptions.length > 0 && (
                  <>
                    {!disabled && hasMultipleFilterOnOptions ? (
                      <SqDropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button className="ml-1 mr-0.5 h-[20px] px-[8px] pt-[2px] inline-flex justify-center items-center gap-[6px] rounded-[4px] border border-[#FFDACC] bg-white text-[#FF4500] text-[10px] font-medium leading-none cursor-pointer whitespace-nowrap focus:outline-none focus:ring-0">
                            {currentFilterOnLabel || "Ad filter"}
                          </button>
                        </DropdownMenuTrigger>
                        <SqDropdownMenuContent
                          align="start"
                          radius={12}
                          className="rounded-[8px]"
                        >
                          <SqDropdownMenuGroup>
                            {FilterOnItems.filter((item) =>
                              availableFilterOnOptions.includes(item.value),
                            ).map((item) => (
                              <SqDropdownMenuItem
                                key={item.key}
                                isSelected={item.value === filter.filter_on}
                                onClick={() => updateFilterOn(filter.id, item.value)}
                                className="cursor-pointer"
                              >
                                <span className={MENU_TEXT_CLS}>{item.label}</span>
                              </SqDropdownMenuItem>
                            ))}
                          </SqDropdownMenuGroup>
                        </SqDropdownMenuContent>
                      </SqDropdownMenu>
                    ) : (
                      <span className="ml-1 mr-0.5 h-[20px] px-[8px] pt-[2px] inline-flex justify-center items-center gap-[6px] rounded-[4px] border border-[#FFDACC] bg-white text-[#FF4500] text-[10px] font-medium leading-none whitespace-nowrap">
                        {currentFilterOnLabel || "Ad filter"}
                      </span>
                    )}
                  </>
                )}

                {/* Block 1: Label (fixed, not editable) */}
                <span
                  className={cn(
                    PILL_LABEL_CLS,
                    isMetricFilter && availableFilterOnOptions.length > 0 && "pl-2",
                  )}
                >
                  {filter?.sub_type === "EXTERNAL" && (
                    <IconDatabase className="h-3.5 w-3.5 text-brand-orange shrink-0 inline mr-1 relative -top-[1px]" />
                  )}
                  {condition.label || filter.label}
                </span>

                {/* Block 2: Operator (editable dropdown) */}
                <OperatorBlock
                  operators={operators}
                  operatorLabel={operatorLabel}
                  conditionFilterType={conditionFilterType}
                  currentOperator={condition.operator}
                  onSelectOperator={(key) => updateFilterOperator(filter.id, key)}
                  disabled={disabled}
                />

                {/* Block 3: Value (editable) — tooltip shows value text on hover only */}
                <HoverOnlyTooltip
                  content={formatConditionValue(
                    condition,
                    conditionFilterType ?? filter.filter_type ?? "",
                    filter,
                    dataConfigFilter,
                    selectedAccount || null,
                    columnOptionMap?.get(condition.key || filter.key) ?? undefined,
                  )}
                >
                  <InlineValueBlock
                    filter={filter}
                    condition={condition}
                    conditionFilterType={conditionFilterType}
                    dataConfigFilter={dataConfigFilter}
                    disabled={disabled}
                    selectedAccount={selectedAccount || null}
                    updateFilterValue={updateFilterValue}
                    updateFilterValueAndOperator={updateFilterValueAndOperator}
                    removeFilter={handleRemoveFilter}
                    allowedDateRange={computeAllowedDateRange(selectedDate, selectedAccount?.time_zone ?? "UTC")}
                    columnOption={columnOptionMap?.get(condition.key || filter.key) ?? undefined}
                  />
                </HoverOnlyTooltip>

                {/* Close button */}
                {!disabled && (
                  <PillCloseButton onClick={() => handleRemoveFilter(filter)} />
                )}
              </motion.div>
            );
          })}
        {/* Advanced Filters pill + popup (only for creating new, not editing) */}
        {showSelectedFilters && showAdvancedFilter && !editingAdvancedFilterId && (
          <motion.div key="advanced-filter-new" {...FILTER_PILL_MOTION}>
            <Popover open modal={false}>
              <PopoverTrigger asChild>
                <div className={FILTER_PILL_BASE}>
                  <span className={PILL_LABEL_CLS}>
                    Advanced filters
                  </span>
                  <PillCloseButton onClick={dismissAdvancedFilter} dataAdvancedDismiss />
                </div>
              </PopoverTrigger>
              <AdvancedFilterPopoverContent
                advancedTempFilter={advancedTempFilter}
                dataConfigFilter={dataConfigFilter}
                selectedDate={selectedDate}
                selectedAccount={selectedAccount || null}
                advancedFilterDropdownOpen={advancedFilterDropdownOpen}
                setAdvancedFilterDropdownOpen={setAdvancedFilterDropdownOpen}
                handleAdvancedUpdateOperator={handleAdvancedUpdateOperator}
                handleAdvancedUpdateValue={handleAdvancedUpdateValue}
                handleAdvancedUpdateValueAndOperator={handleAdvancedUpdateValueAndOperator}
                handleAdvancedRemoveCondition={handleAdvancedRemoveCondition}
                handleAdvancedAddFilterKeySelect={handleAdvancedAddFilterKeySelect}
                onAdvancedTempFilterChange={onAdvancedTempFilterChange}
                updateTempFilterWithFilterOn={updateTempFilterWithFilterOn}
                commitAdvancedFilter={commitAdvancedFilter}
                dismissAdvancedFilter={dismissAdvancedFilter}
                advancedOpenedAtRef={advancedOpenedAtRef}
                columnOptionMap={columnOptionMap}
              />
            </Popover>
          </motion.div>
        )}
      </AnimatePresence>

      {/*Add filter*/}
      {!disabled && showAddFilters && (
        <div className="ml-auto">
          <DropdownMenu
            open={openFilterId === "new"}
            onOpenChange={(open) => {
              if (open) {
                setOpenFilterId("new");
              } else {
                setOpenFilterId(null);
              }
            }}
          >
            <DropdownMenuTrigger className="focus:outline-none focus:ring-0">
              {customTrigger ? (
                customTrigger
              ) : (
                <SqButton
                  variant="outline"
                  size={"md"}
                  className="h-7 w-7"
                  radius={6}
                  shadow={"sm"}
                >
                  <RiAddLine size={14} className="text-brand-orange shrink-0" />
                </SqButton>
              )}
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="start"
              className="w-auto p-0 rounded-[12px] border-0 overflow-visible"
              collisionPadding={10}
            >
              <AddFilterKeyDropdown
                dataConfigFilter={dataConfigFilter}
                handleFilterClick={handleAddFilterKeySelect}
                onAssetFilterCommit={handleAssetFilterCommit}
                onAssetFilterRemoveValue={handleAssetFilterRemoveValue}
                onAdvancedFiltersClick={hideAdvancedFilters ? undefined : () => {
                  advancedOpenedAtRef.current = Date.now();
                  onShowAdvancedFilterChange?.(true);
                  setOpenFilterId(null);
                }}
                clearSelection={
                  clearSelection && Boolean(selectedFilters?.length)
                    ? () => {
                        onFilterChange([]);
                        setOpenFilterId(null);
                      }
                    : undefined
                }
                selectedAccount={selectedAccount || null}
                allowedDateRange={computeAllowedDateRange(selectedDate, selectedAccount?.time_zone ?? "UTC")}
                columnOptionMap={columnOptionMap}
              />
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}
    </div>
  );
};

export default Filters;

const AdvancedFilterPopoverContent = ({
  advancedTempFilter,
  dataConfigFilter,
  selectedDate,
  selectedAccount,
  advancedFilterDropdownOpen,
  setAdvancedFilterDropdownOpen,
  handleAdvancedUpdateOperator,
  handleAdvancedUpdateValue,
  handleAdvancedUpdateValueAndOperator,
  handleAdvancedRemoveCondition,
  handleAdvancedAddFilterKeySelect,
  onAdvancedTempFilterChange,
  updateTempFilterWithFilterOn,
  commitAdvancedFilter,
  dismissAdvancedFilter,
  editingFilterId,
  selectedFilters,
  advancedOpenedAtRef,
  columnOptionMap,
}: {
  advancedTempFilter?: SectionFilter;
  dataConfigFilter: DataConfigurationsFilter;
  selectedDate: ReportDate | null;
  selectedAccount: { time_zone?: string } | null;
  advancedFilterDropdownOpen: boolean;
  setAdvancedFilterDropdownOpen: (open: boolean) => void;
  handleAdvancedUpdateOperator: (index: number, key: string) => void;
  handleAdvancedUpdateValue: (
    index: number,
    newValue:
      | string
      | string[]
      | number
      | { min: number; max: number }
      | { start_date: string; end_date: string; label: TimeInterval },
  ) => void;
  handleAdvancedUpdateValueAndOperator: (
    index: number,
    newValue: string | string[],
    newOperator: string,
  ) => void;
  handleAdvancedRemoveCondition: (index: number) => void;
  handleAdvancedAddFilterKeySelect: (
    item: NonNullable<DataConfigurationsFilter["filter_choices"]>[number],
    child?: NonNullable<(typeof item)["children"]>[number],
  ) => void;
  onAdvancedTempFilterChange?: (filter: SectionFilter | undefined) => void;
  updateTempFilterWithFilterOn: (
    filter: SectionFilter | undefined,
  ) => SectionFilter | undefined;
  commitAdvancedFilter: () => void;
  dismissAdvancedFilter: () => void;
  editingFilterId?: string | null;
  selectedFilters?: SectionFilter[] | null;
  advancedOpenedAtRef: React.MutableRefObject<number>;
  columnOptionMap?: Map<string, ColumnOption> | null;
}) => (
  <PopoverContent
    align="start"
    side="bottom"
    sideOffset={6}
    className="w-auto p-0 border-0 bg-transparent shadow-none"
    onOpenAutoFocus={(e) => e.preventDefault()}
    onInteractOutside={(e) => {
      e.preventDefault();
      const target = e.target as HTMLElement | null;
      if (target?.closest("[data-advanced-dismiss]")) return;
      if (Date.now() - advancedOpenedAtRef.current < 300) return;
      if (!advancedTempFilter?.conditions?.length) {
        dismissAdvancedFilter();
        return;
      }
      const originalFilter = editingFilterId
        ? selectedFilters?.find((f) => f.id === editingFilterId)
        : undefined;
      if (
        originalFilter &&
        JSON.stringify(originalFilter.conditions) ===
          JSON.stringify(advancedTempFilter.conditions)
      ) {
        dismissAdvancedFilter();
        return;
      }
      if (advancedTempFilter.conditions.some(validateCondition)) {
        commitAdvancedFilter();
      } else {
        dismissAdvancedFilter();
      }
    }}
  >
    <div className="rounded-[8px] px-3 py-2.5 bg-white border border-[#E8E8E8] shadow-md flex flex-col gap-2 min-w-[350px]">
      <AnimatePresence mode="popLayout" initial={false}>
        {advancedTempFilter?.conditions?.map((condition, index) => {
          const conditionFilterType = resolveConditionFilterType(condition, advancedTempFilter, dataConfigFilter);

          const { operators, operatorLabel } = getFilteredOperators(
            conditionFilterType, condition.key, advancedTempFilter.key,
            condition.value, condition.operator, dataConfigFilter,
          );

          return (
            <motion.div
              key={index}
              {...FILTER_ROW_MOTION}
              className="flex items-center gap-2"
            >
              {index > 0 && (
                <span className={OR_LABEL_CLS}>
                  or
                </span>
              )}
              <div className={FILTER_PILL_BASE}>
                <span className={PILL_LABEL_CLS}>
                  {condition.label || advancedTempFilter.label}
                </span>
                <OperatorBlock
                  operators={operators}
                  operatorLabel={operatorLabel}
                  conditionFilterType={conditionFilterType}
                  currentOperator={condition.operator}
                  onSelectOperator={(key) => handleAdvancedUpdateOperator(index, key)}
                />
                <HoverOnlyTooltip
                  content={formatConditionValue(condition, conditionFilterType || "", advancedTempFilter, dataConfigFilter, selectedAccount || null, columnOptionMap?.get(condition.key || advancedTempFilter.key) ?? undefined)}
                >
                  <InlineValueBlock
                    filter={advancedTempFilter}
                    condition={condition}
                    conditionFilterType={conditionFilterType}
                    dataConfigFilter={dataConfigFilter}
                    disabled={false}
                    selectedAccount={selectedAccount || null}
                    updateFilterValue={(_filterId, newValue) =>
                      handleAdvancedUpdateValue(index, newValue)
                    }
                    updateFilterValueAndOperator={(_filterId, newValue, newOperator) =>
                      handleAdvancedUpdateValueAndOperator(index, newValue, newOperator)
                    }
                    allowedDateRange={computeAllowedDateRange(selectedDate, selectedAccount?.time_zone ?? "UTC")}
                    columnOption={columnOptionMap?.get(condition.key || advancedTempFilter.key) ?? undefined}
                  />
                </HoverOnlyTooltip>
                <PillCloseButton onClick={() => handleAdvancedRemoveCondition(index)} />
              </div>
            </motion.div>
          );
        })}
      </AnimatePresence>
      <div className="flex items-center gap-2">
        {advancedTempFilter?.conditions && advancedTempFilter.conditions.length > 0 && (
          <span className={OR_LABEL_CLS}>
            or
          </span>
        )}
        <DropdownMenu
          open={advancedFilterDropdownOpen}
          onOpenChange={setAdvancedFilterDropdownOpen}
        >
          <DropdownMenuTrigger className="focus:outline-none focus:ring-0">
            <SqButton
              variant="outline"
              hasOutline
              bgColor="white"
              hoverBgColor="#FBFBFB"
              shadow={"sm"}
              radius={6}
              className="cursor-pointer h-7 px-3"
            >
              <RiFilter3Line size={14} color="#9AA3BD" className="shrink-0" />
              <span className="mt-[2px] text-[12px] text-brand-primary-800 font-medium">
                Add Filter
              </span>
            </SqButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="start"
            className="w-auto p-0 rounded-[12px] border-0 overflow-visible"
            collisionPadding={10}
          >
            <AddFilterKeyDropdown
              dataConfigFilter={dataConfigFilter}
              handleFilterClick={handleAdvancedAddFilterKeySelect}
              onAssetFilterCommit={(item, value, parentKey, operatorKey) => {
                const isAssetFilter = ASSET_FILTER_KEYS.includes(item.key);

                if (isAssetFilter) {
                  const newValues = Array.isArray(value) ? value : [value];
                  const existingCondition = advancedTempFilter?.conditions?.find(
                    (c) =>
                      (c.key === item.key || (!c.key && advancedTempFilter.key === item.key)) &&
                      ALL_ASSET_OPS.includes(c.operator ?? ""),
                  );

                  if (existingCondition && advancedTempFilter) {
                    const prev = existingCondition.value;
                    const prevValues = Array.isArray(prev) ? (prev as string[]) : prev ? [prev as string] : [];
                    const allValues = [...new Set([...prevValues, ...newValues])];
                    const useArray = allValues.length > 1;
                    const updatedFilter = {
                      ...advancedTempFilter,
                      conditions: advancedTempFilter.conditions?.map((c) =>
                        c === existingCondition
                          ? {
                              ...c,
                              operator: resolveAssetOp(c.operator, allValues.length),
                              value: useArray ? allValues : allValues[0],
                            }
                          : c,
                      ),
                    };
                    const withFilterOn = updateTempFilterWithFilterOn(updatedFilter);
                    onAdvancedTempFilterChange?.(withFilterOn ?? updatedFilter);
                  } else {
                    const isSingle = newValues.length === 1;
                    const newCondition = {
                      operator: isSingle ? "CONTAIN" : "CONTAIN_ANY",
                      value: isSingle ? newValues[0] : newValues,
                      label: item.label,
                      key: item.key,
                      parent_key: parentKey || item.key,
                    };
                    if (!advancedTempFilter) {
                      const newFilter: SectionFilter = {
                        id: uuidv4(),
                        key: item.key,
                        field: "field" in item ? item.field : "",
                        label: item.label,
                        filter_type: item.filter_type,
                        sub_type:
                          "sub_type" in item
                            ? (item as { sub_type?: string }).sub_type
                            : undefined,
                        type: item?.type,
                        conditions: [newCondition],
                      };
                      const withFilterOn = updateTempFilterWithFilterOn(newFilter);
                      onAdvancedTempFilterChange?.(withFilterOn ?? newFilter);
                    } else {
                      const updatedFilter = {
                        ...advancedTempFilter,
                        conditions: [...(advancedTempFilter.conditions || []), newCondition],
                      };
                      const withFilterOn = updateTempFilterWithFilterOn(updatedFilter);
                      onAdvancedTempFilterChange?.(withFilterOn ?? updatedFilter);
                    }
                  }
                  return;
                }

                const resolvedOperator = operatorKey
                  ? operatorKey
                  : (dataConfigFilter?.filter_types.find(
                      (type) => type.type === item.filter_type,
                    )?.operators[0]?.key ?? "");
                const newCondition = {
                  operator: resolvedOperator,
                  value,
                  label: item.label,
                  key: item.key,
                  parent_key: parentKey || item.key,
                };
                if (!advancedTempFilter) {
                  const newFilter: SectionFilter = {
                    id: uuidv4(),
                    key: item.key,
                    field: "field" in item ? item.field : "",
                    label: item.label,
                    filter_type: item.filter_type,
                    sub_type:
                      "sub_type" in item
                        ? (item as { sub_type?: string }).sub_type
                        : undefined,
                    type: item?.type,
                    conditions: [newCondition],
                  };
                  const withFilterOn = updateTempFilterWithFilterOn(newFilter);
                  onAdvancedTempFilterChange?.(withFilterOn ?? newFilter);
                } else {
                  const updatedFilter = {
                    ...advancedTempFilter,
                    conditions: [...(advancedTempFilter.conditions || []), newCondition],
                  };
                  const withFilterOn = updateTempFilterWithFilterOn(updatedFilter);
                  onAdvancedTempFilterChange?.(withFilterOn ?? updatedFilter);
                }
                setAdvancedFilterDropdownOpen(false);
              }}
              onAssetFilterRemoveValue={(item, valueToRemove) => {
                if (!advancedTempFilter) return;
                const existingCondition = advancedTempFilter.conditions?.find(
                  (c) =>
                    (c.key === item.key || (!c.key && advancedTempFilter.key === item.key)) &&
                    ALL_ASSET_OPS.includes(c.operator ?? ""),
                );
                if (!existingCondition) return;
                const prev = existingCondition.value;
                const prevValues = Array.isArray(prev) ? (prev as string[]) : prev ? [prev as string] : [];
                const remaining = prevValues.filter((v) => v !== valueToRemove);
                if (remaining.length === 0) {
                  const updatedFilter = {
                    ...advancedTempFilter,
                    conditions: advancedTempFilter.conditions?.filter((c) => c !== existingCondition),
                  };
                  onAdvancedTempFilterChange?.(updatedFilter);
                } else {
                  const useSingle = remaining.length === 1;
                  const updatedFilter = {
                    ...advancedTempFilter,
                    conditions: advancedTempFilter.conditions?.map((c) =>
                      c === existingCondition
                        ? {
                            ...c,
                            operator: resolveAssetOp(c.operator, remaining.length),
                            value: useSingle ? remaining[0] : remaining,
                          }
                        : c,
                    ),
                  };
                  onAdvancedTempFilterChange?.(updatedFilter);
                }
              }}
              selectedAccount={selectedAccount || null}
              allowedDateRange={computeAllowedDateRange(selectedDate, selectedAccount?.time_zone ?? "UTC")}
              columnOptionMap={columnOptionMap}
            />
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  </PopoverContent>
);

type FilterDropdownContentProps = {
  tempFilter: SectionFilter | undefined;
  setTempFilter: (
    filter:
      | ((prev: SectionFilter | undefined) => SectionFilter | undefined)
      | SectionFilter
      | undefined,
  ) => void;
  dataConfigFilter: DataConfigurationsFilter | null;
  onApply: () => void;
  onCancel: () => void;
  isKeySelectionMode: boolean;
  setIsKeySelectionMode: (mode: boolean) => void;
  availableFilterOnOptions: FilterOn[];
  hasMultipleFilterOnOptions: boolean;
  updateTempFilterWithFilterOn: (
    filter: SectionFilter | undefined,
  ) => SectionFilter | undefined;
  allowedDateRange: { first_allowed_date?: Date; last_allowed_date?: Date } | null;
  clearSelection?: () => void;
};

const getActiveFilter = (filter?: SectionFilter) => {
  return filter;
};

// Helper function to validate a single condition
const validateCondition = (condition: {
  operator: string;
  value:
    | string
    | string[]
    | number
    | { min: number; max: number }
    | { start_date: string; end_date: string; label: TimeInterval };
  key?: string;
  label?: string;
}) => {
  // Check if operator exists
  if (!condition.operator) return false;

  // Check value based on type
  if (typeof condition.value === "number")
    return condition.value !== undefined && condition.value !== null;
  else if (typeof condition.value === "string") return condition.value.trim() !== "";
  else if (typeof condition.value === "boolean")
    return condition.value === true || condition.value === false;
  else if (Array.isArray(condition.value)) return condition.value.length > 0;
  else if (typeof condition.value === "object" && condition.value !== null) {
    // Handle date range objects
    if ("start_date" in condition.value && "end_date" in condition.value)
      return !!(condition.value.start_date && condition.value.end_date);
    // Handle min/max objects
    if ("min" in condition.value && "max" in condition.value)
      return !!(condition.value.min !== undefined && condition.value.max !== undefined);
  }
  return false;
};

// Shared helpers for condition filter type and value formatting
function resolveConditionFilterType(
  condition: NonNullable<SectionFilter["conditions"]>[number],
  filter: SectionFilter,
  dataConfigFilter: DataConfigurationsFilter | null,
): string {
  if (!condition.key || condition.key === filter.key) return filter.filter_type ?? "";
  const direct = dataConfigFilter?.filter_choices.find(
    (c) => c.key === condition.key,
  )?.filter_type;
  if (direct) return direct;
  const parent = dataConfigFilter?.filter_choices
    .filter((c): c is typeof c & { children: { key: string }[] } => "children" in c)
    .find((c) => c.children?.some((ch) => ch.key === condition.key));
  const child = parent?.children?.find((c) => c.key === condition.key);
  return (
    (child as { filter_type?: string } | undefined)?.filter_type ??
    filter.filter_type ??
    ""
  );
}

function computeAllowedDateRange(
  selectedDate: ReportDate | null,
  timeZone: string,
): { first_allowed_date?: Date; last_allowed_date?: Date } | null {
  if (!selectedDate) return null;
  const endDate = new TZDate(
    fromZonedTime(selectedDate.end_date, timeZone),
    timeZone,
  );
  endDate.setHours(23, 59, 59, 999);
  return { last_allowed_date: endDate };
}

function getFilteredOperators(
  conditionFilterType: string | undefined,
  conditionKey: string | undefined,
  filterKey: string,
  conditionValue: unknown,
  conditionOperator: string,
  dataConfigFilter: DataConfigurationsFilter,
) {
  const rawOperators = dataConfigFilter.filter_types.find(
    (t) => t.type === conditionFilterType,
  )?.operators;
  const allOperators = rawOperators?.filter((op) => op.key !== "BETWEEN");
  const isAssetFilter = ASSET_FILTER_KEYS.includes(conditionKey || filterKey);
  const isMultiValue = Array.isArray(conditionValue);
  const operators = isAssetFilter
    ? allOperators?.filter((op) =>
        isMultiValue
          ? MULTI_ASSET_OPS.includes(op.key)
          : SINGLE_ASSET_OPS.includes(op.key),
      )
    : allOperators;
  const valueCount = Array.isArray(conditionValue) ? conditionValue.length : 1;
  const finalOperators = operators?.map((op) => {
    const override = resolveInOpLabel(op.key, valueCount);
    return override ? { ...op, label: override } : op;
  });
  const operatorLabel =
    resolveInOpLabel(conditionOperator, valueCount) ||
    (allOperators ?? operators)?.find((op) => op.key === conditionOperator)?.label ||
    conditionOperator;
  return { allOperators, operators: finalOperators, operatorLabel };
}

function resolveFilterChoiceOptions(
  conditionKey: string,
  dataConfigFilter: DataConfigurationsFilter | null,
): Array<{ key: string; label: string }> | undefined {
  const choice = dataConfigFilter?.filter_choices.find((c) => c.key === conditionKey);
  let options = choice?.options;
  if (!options) {
    const parent = dataConfigFilter?.filter_choices
      .filter(
        (
          c,
        ): c is typeof c & {
          children: { key: string; options?: Array<{ key: string; label: string }> }[];
        } => "children" in c,
      )
      .find((c) => c.children?.some((ch) => ch.key === conditionKey));
    options = parent?.children?.find((ch) => ch.key === conditionKey)?.options;
  }
  return options as Array<{ key: string; label: string }> | undefined;
}

function formatConditionValue(
  condition: NonNullable<SectionFilter["conditions"]>[number],
  conditionFilterType: string,
  filter: SectionFilter,
  dataConfigFilter: DataConfigurationsFilter | null,
  selectedAccount: { time_zone?: string } | null,
  columnOption?: ColumnOption,
): string {
  const pfx = columnOption?.prefix || "";
  const sfx = columnOption?.suffix || "";
  if (conditionFilterType === "date") {
    try {
      const dateValue = condition.value as { start_date?: string; end_date?: string };
      return dateValue?.start_date && dateValue?.end_date
        ? formatDateRange({
            start_date: dateValue.start_date,
            end_date: dateValue.end_date,
            timezone: selectedAccount?.time_zone,
          })
        : "Select date range";
    } catch {
      return "Select date range";
    }
  }
  if (Array.isArray(condition.value)) {
    if (condition.key === "account" || filter.key === "account") {
      const labels = (condition.value as string[]).map((id) => {
        const opt = dataConfigFilter?.filter_choices
          .find((c) => c.key === "account")
          ?.options?.find((o) => o.key === id);
        return opt ? opt.label : id;
      });
      return labels.join(", ");
    }
    return (condition.value as string[]).join(", ");
  }
  if (typeof condition.value === "object" && condition.value !== null) {
    const v = condition.value as Record<string, unknown>;
    if ("start_date" in v && "end_date" in v) {
      return formatDateRange({
        start_date: v.start_date as string,
        end_date: v.end_date as string,
        timezone: selectedAccount?.time_zone,
      });
    }
    if ("min" in v && "max" in v) return `${pfx}${v.min}${sfx} - ${pfx}${v.max}${sfx}`;
    return JSON.stringify(condition.value);
  }
  if (conditionFilterType === "boolean") {
    const options = resolveFilterChoiceOptions(condition.key ?? "", dataConfigFilter);
    const match = options?.find((o) => o.key === condition.value);
    return match ? match.label : String(condition.value);
  }
  return `${pfx}${condition.value}${sfx}`;
}

// Helper function to generate full filter text for tooltip
const getFilterTooltipText = (
  filter: SectionFilter,
  dataConfigFilter: DataConfigurationsFilter | null,
  selectedAccount: { time_zone?: string } | null,
  columnOptionMap?: Map<string, ColumnOption> | null,
) => {
  if (!filter.conditions) return filter.label || "";
  return filter.conditions
    .map((condition, index) => {
      const conditionFilterType = resolveConditionFilterType(
        condition,
        filter,
        dataConfigFilter,
      );
      const valCount = Array.isArray(condition.value) ? condition.value.length : 1;
      const operatorLabel =
        resolveInOpLabel(condition.operator, valCount) ||
        dataConfigFilter?.filter_types
          .find((t) => t.type === conditionFilterType)
          ?.operators.find((op) => op.key === condition.operator)?.label ||
        condition.operator.toLowerCase();
      const valueText = formatConditionValue(
        condition,
        conditionFilterType,
        filter,
        dataConfigFilter,
        selectedAccount,
        columnOptionMap?.get(condition.key || filter.key) ?? undefined,
      );
      return `${index > 0 ? " OR " : ""}${condition.label} ${operatorLabel} ${valueText}`;
    })
    .join("");
};

const getFilterSummaryRichText = (
  filter: SectionFilter,
  dataConfigFilter: DataConfigurationsFilter | null,
  selectedAccount: { time_zone?: string } | null,
  maxConditions = 2,
  columnOptionMap?: Map<string, ColumnOption> | null,
) => {
  if (!filter.conditions) return { content: <>{filter.label || ""}</>, overflow: 0 };
  const total = filter.conditions.length;
  const visible = filter.conditions.slice(0, maxConditions);
  const overflow = Math.max(0, total - maxConditions);

  const content = visible.map((condition, index) => {
    const conditionFilterType = resolveConditionFilterType(
      condition,
      filter,
      dataConfigFilter,
    );
    const valCount = Array.isArray(condition.value) ? condition.value.length : 1;
    const operatorLabel =
      resolveInOpLabel(condition.operator, valCount) ||
      dataConfigFilter?.filter_types
        .find((t) => t.type === conditionFilterType)
        ?.operators.find((op) => op.key === condition.operator)?.label ||
      condition.operator.toLowerCase();
    const valueText = formatConditionValue(
      condition,
      conditionFilterType,
      filter,
      dataConfigFilter,
      selectedAccount,
      columnOptionMap?.get(condition.key || filter.key) ?? undefined,
    );

    return (
      <Fragment key={index}>
        {index > 0 && (
          <span className="text-[#FF8F66] lowercase font-medium px-[2px]">{" or "}</span>
        )}
        {condition.label}{" "}
        <span className="text-[#FF8F66] lowercase">{operatorLabel}</span> {valueText}
      </Fragment>
    );
  });

  return { content, overflow };
};

// Helper function to get the correct initial value based on filter type
const getInitialValueForFilterType = (filterType: string, type?: string) => {
  // For multi-select filters, initialize with empty array
  if (type === "multi_select" || filterType === "option") {
    return [];
  }
  // For all other filter types, initialize with empty string
  return "";
};

const adjustDateWithinAllowedRange = (
  date: Date,
  currentTime: Date,
  allowedDateRange?: { first_allowed_date?: Date; last_allowed_date?: Date } | null,
) => {
  if (!allowedDateRange) return date;
  let adjustedDate = new Date(date);

  // Copy current time
  adjustedDate.setHours(
    currentTime.getHours(),
    currentTime.getMinutes(),
    currentTime.getSeconds(),
    currentTime.getMilliseconds(),
  );

  // Enforce date bounds
  if (
    allowedDateRange?.first_allowed_date &&
    adjustedDate < allowedDateRange.first_allowed_date
  ) {
    adjustedDate = allowedDateRange.first_allowed_date;
  }
  if (
    allowedDateRange?.last_allowed_date &&
    adjustedDate > allowedDateRange.last_allowed_date
  ) {
    adjustedDate = allowedDateRange.last_allowed_date;
  }

  return adjustedDate;
};

// Common empty state component for search results
const SearchEmptyState = () => (
  <div className="flex flex-col items-center justify-center py-4 px-4">
    <div className="text-center">
      <div className="text-brand-primary-new/40 text-[14px] font-medium mb-1">
        No results found
      </div>
      <div className="text-brand-primary-new/40 text-[12px] opacity-70">
        Try searching for something else
      </div>
    </div>
  </div>
);

// Standalone add-filter key picker (wraps FilterDropdown with its own state)
const ASSET_FILTER_KEYS = ["ad_name", "adset_name", "campaign_name"];
const SINGLE_ASSET_OPS = ["CONTAIN", "NOT_CONTAIN"];
const MULTI_ASSET_OPS = ["CONTAIN_ANY", "CONTAIN_NONE"];
const ALL_ASSET_OPS = [...SINGLE_ASSET_OPS, ...MULTI_ASSET_OPS];

function resolveAssetOp(currentOp: string, count: number): string {
  const neg = currentOp === "NOT_CONTAIN" || currentOp === "CONTAIN_NONE";
  return count > 1 ? (neg ? "CONTAIN_NONE" : "CONTAIN_ANY") : neg ? "NOT_CONTAIN" : "CONTAIN";
}

function resolveInOpLabel(key: string, multiCount: number): string | null {
  if (key === "IN") return multiCount > 1 ? "Is any of" : "Is";
  if (key === "NOT_IN") return multiCount > 1 ? "Is none of" : "Isn't";
  return null;
}

// Session-level cache for asset search results
const assetCache = new Map<string, string[]>();

function getCacheKey(accountId: string, assetType: string, searchTerm: string) {
  return `${accountId}:${assetType}:${searchTerm}`;
}

async function fetchAssetsWithCache(
  accountId: string,
  assetType: string,
  searchTerm: string,
  options?: { signal?: AbortSignal },
): Promise<string[]> {
  const key = getCacheKey(accountId, assetType, searchTerm);
  const cached = assetCache.get(key);
  if (cached) return cached;
  const results = await fetchAssets(accountId, assetType, searchTerm, options);
  assetCache.set(key, results);
  return results;
}

const EXCLUDED_SIDE_PANEL_TYPES = ["date"];
const METRIC_SIDE_PANEL_TYPES = ["number", "between"];
const DAYS_FROM_LAUNCH_KEYS = ["launch_window"];
const DAYS_FROM_LAUNCH_LABELS = ["days since launch"];

function isDaysFromLaunch(choice: { key: string; label?: string }) {
  return (
    DAYS_FROM_LAUNCH_KEYS.includes(choice.key) ||
    DAYS_FROM_LAUNCH_LABELS.includes(choice.label?.toLowerCase() ?? "")
  );
}
const DAYS_FROM_LAUNCH_PRESETS = [
  { label: "First 1 day", value: 1 },
  { label: "First 2 days", value: 2 },
  { label: "First 3 days", value: 3 },
  { label: "First 7 days", value: 7 },
  { label: "First 14 days", value: 14 },
  { label: "First 30 days", value: 30 },
];
const AD_STARTED_KEYS = ["start_date"];
const AD_STARTED_LABELS = ["ad started"];

function isAdStarted(choice: { key: string; label?: string }) {
  return (
    AD_STARTED_KEYS.includes(choice.key) ||
    AD_STARTED_LABELS.includes(choice.label?.toLowerCase() ?? "")
  );
}
const AD_STARTED_PRESETS = [
  { label: "1 day ago", daysAgo: 1 },
  { label: "3 days ago", daysAgo: 3 },
  { label: "1 week ago", daysAgo: 7 },
  { label: "1 month ago", daysAgo: 30 },
  { label: "3 months ago", daysAgo: 90 },
  { label: "6 months ago", daysAgo: 180 },
  { label: "1 years ago", daysAgo: 365 },
];

const AddFilterKeyDropdown = ({
  dataConfigFilter,
  handleFilterClick,
  onAssetFilterCommit,
  onAssetFilterRemoveValue,
  onAdvancedFiltersClick,
  clearSelection,
  selectedAccount,
  allowedDateRange,
  columnOptionMap,
}: {
  dataConfigFilter: DataConfigurationsFilter | null;
  handleFilterClick: (
    item: NonNullable<DataConfigurationsFilter["filter_choices"]>[number],
    child?: NonNullable<(typeof item)["children"]>[number],
  ) => void;
  onAssetFilterCommit: (
    item: NonNullable<DataConfigurationsFilter["filter_choices"]>[number],
    value: string | string[],
    parentKey?: string,
    operatorKey?: string,
    existingFilterId?: string,
  ) => string | undefined | void;
  onAssetFilterRemoveValue?: (
    item: NonNullable<DataConfigurationsFilter["filter_choices"]>[number],
    value: string,
  ) => void;
  onAdvancedFiltersClick?: () => void;
  clearSelection?: () => void;
  selectedAccount: { time_zone?: string } | null;
  allowedDateRange: { first_allowed_date?: Date; last_allowed_date?: Date } | null;
  columnOptionMap?: Map<string, ColumnOption> | null;
}) => {
  const { accountId } = useParams();
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [expandedChoices, setExpandedChoices] = useState<Record<string, boolean>>({});
  const [activeAssetKey, setActiveAssetKey] = useState<string | null>(null);

  // Prefetch base asset lists when the Add Filter dropdown mounts
  useEffect(() => {
    if (!accountId) return;
    const controller = new AbortController();
    for (const assetType of ["ad", "adset", "campaign"]) {
      fetchAssetsWithCache(accountId, assetType, "", {
        signal: controller.signal,
      }).catch(() => {
        // ignore prefetch errors
      });
    }
    return () => controller.abort();
  }, [accountId]);

  useEffect(() => {
    if (!searchTerm.trim()) {
      setExpandedChoices({});
      return;
    }
    const newExpanded: Record<string, boolean> = {};
    const searchLower = searchTerm.toLowerCase();
    dataConfigFilter?.filter_choices
      .filter((choice) => "children" in choice)
      .forEach((choice) => {
        if (
          choice.children?.some((child) =>
            child.label.toLowerCase().includes(searchLower),
          )
        ) {
          newExpanded[choice.key] = true;
        }
      });
    setExpandedChoices(newExpanded);
  }, [searchTerm, dataConfigFilter]);

  return (
    <FilterDropdown
      searchTerm={searchTerm}
      setSearchTerm={setSearchTerm}
      dataConfigFilter={dataConfigFilter}
      expandedChoices={expandedChoices}
      setExpandedChoices={setExpandedChoices}
      handleFilterClick={handleFilterClick}
      onAssetFilterCommit={onAssetFilterCommit}
      onAssetFilterRemoveValue={onAssetFilterRemoveValue}
      onAdvancedFiltersClick={onAdvancedFiltersClick}
      activeAssetKey={activeAssetKey}
      setActiveAssetKey={setActiveAssetKey}
      clearSelection={clearSelection}
      selectedAccount={selectedAccount}
      allowedDateRange={allowedDateRange}
      columnOptionMap={columnOptionMap}
    />
  );
};

// Value display text helper (uses formatConditionValue where same; keeps display-specific truncation/empty handling)
const getValueDisplayText = (
  condition: NonNullable<SectionFilter["conditions"]>[number],
  conditionFilterType: string | undefined,
  filter: SectionFilter,
  dataConfigFilter: DataConfigurationsFilter,
  selectedAccount: { time_zone?: string } | null,
  columnOption?: ColumnOption,
): string => {
  if (condition.value === "" || condition.value === undefined) return "Set value";
  if (
    conditionFilterType === "date" ||
    (typeof condition.value === "object" &&
      condition.value !== null &&
      !Array.isArray(condition.value))
  ) {
    return formatConditionValue(
      condition,
      conditionFilterType ?? filter.filter_type ?? "",
      filter,
      dataConfigFilter,
      selectedAccount,
      columnOption,
    );
  }
  if (Array.isArray(condition.value)) {
    const values = condition.value;
    if (values.length === 0) return "Select value";
    if (filter.key === "account") {
      const accountLabels = values.map((accountId) => {
        const opt = dataConfigFilter?.filter_choices
          .find((c) => c.key === "account")
          ?.options?.find((o) => o.key === accountId);
        return opt ? opt.label : accountId;
      });
      const shown = accountLabels.slice(0, 1);
      const rest = accountLabels.length - 1;
      return `${shown.join(", ")}${rest > 0 ? ` +${rest} more` : ""}`;
    }
    const shown = values.slice(0, 2);
    const rest = values.length - 2;
    return `${shown.join(", ")}${rest > 0 ? ` +${rest} more` : ""}`;
  }
  if (conditionFilterType === "boolean") {
    const options = resolveFilterChoiceOptions(condition.key ?? "", dataConfigFilter);
    const match = options?.find((o) => o.key === condition.value);
    if (match) return match.label;
  }
  const pfx = columnOption?.prefix || "";
  const sfx = columnOption?.suffix || "";
  return `${pfx}${condition.value}${sfx}`;
};

// Inline value editing block for filter chips
type InlineValueBlockProps = {
  filter: SectionFilter;
  condition: NonNullable<SectionFilter["conditions"]>[number];
  conditionFilterType: string | undefined;
  dataConfigFilter: DataConfigurationsFilter;
  disabled: boolean;
  selectedAccount: { time_zone?: string } | null;
  updateFilterValue: (
    filterId: string | undefined,
    newValue:
      | string
      | string[]
      | number
      | { min: number; max: number }
      | { start_date: string; end_date: string; label: TimeInterval },
  ) => void;
  updateFilterValueAndOperator: (
    filterId: string | undefined,
    newValue: string | string[],
    newOperator: string,
  ) => void;
  removeFilter?: (filter: SectionFilter) => void;
  allowedDateRange: { first_allowed_date?: Date; last_allowed_date?: Date } | null;
  columnOption?: ColumnOption;
};

const InlineValueBlock = ({
  filter,
  condition,
  conditionFilterType,
  dataConfigFilter,
  disabled,
  selectedAccount,
  updateFilterValue,
  updateFilterValueAndOperator,
  removeFilter,
  allowedDateRange,
  columnOption,
}: InlineValueBlockProps) => {
  const { accountId } = useParams();
  const [localValue, setLocalValue] = useState(condition.value);
  const [dateDialogOpen, setDateDialogOpen] = useState(false);

  // Sync local value when filter changes externally
  useEffect(() => {
    setLocalValue(condition.value);
  }, [condition.value]);

  const valueDisplay = getValueDisplayText(
    condition,
    conditionFilterType,
    filter,
    dataConfigFilter,
    selectedAccount,
    columnOption,
  );

  const chipValueClass =
    "px-2 py-1 text-brand-orange text-[11px] font-normal leading-[19.157px] tracking-[0.24px] border-l border-brand-orange/20 max-w-[40ch] truncate whitespace-nowrap";

  if (disabled) {
    return <span className={chipValueClass}>{valueDisplay}</span>;
  }

  // Date type: use Dialog with DateRangePicker
  if (conditionFilterType === "date") {
    return (
      <Dialog open={dateDialogOpen} onOpenChange={setDateDialogOpen}>
        <DialogTrigger asChild>
          <button
            className={cn(
              chipValueClass,
              "cursor-pointer hover:bg-[#FCE5D9] transition-colors",
            )}
          >
            {valueDisplay}
          </button>
        </DialogTrigger>
        <DialogContent className="p-0 border-none rounded-[12px] w-full" hideCloseButton>
          <DateRangePicker
            singleSelect={
              condition.operator === "DATE_ON_OR_AFTER" ||
              condition.operator === "DATE_ON_OR_BEFORE"
            }
            first_allowed_date={allowedDateRange?.first_allowed_date}
            last_allowed_date={allowedDateRange?.last_allowed_date}
            selected={
              condition.value
                ? {
                    from: fromZonedTime(
                      (condition.value as { start_date: string }).start_date,
                      selectedAccount?.time_zone ?? "UTC",
                    ),
                    to: fromZonedTime(
                      (condition.value as { end_date: string }).end_date,
                      selectedAccount?.time_zone ?? "UTC",
                    ),
                    label:
                      (condition.value as { label: TimeInterval }).label ??
                      TimeInterval.CUSTOM,
                  }
                : null
            }
            dateAggregate=""
            onSelect={() => {}}
            onApply={(range) => {
              if (!range) return;
              const currentTime = new Date();
              const adjustedStart = adjustDateWithinAllowedRange(
                range.from,
                currentTime,
                allowedDateRange,
              );
              const adjustedEnd = adjustDateWithinAllowedRange(
                range.to,
                currentTime,
                allowedDateRange,
              );
              updateFilterValue(filter.id, {
                start_date: adjustedStart.toLocaleDateString("en-CA", {
                  timeZone: selectedAccount?.time_zone ?? "UTC",
                }),
                end_date: adjustedEnd.toLocaleDateString("en-CA", {
                  timeZone: selectedAccount?.time_zone ?? "UTC",
                }),
                label: range.label ?? TimeInterval.CUSTOM,
              });
              setDateDialogOpen(false);
            }}
            timezone={selectedAccount?.time_zone ?? "UTC"}
          />
        </DialogContent>
      </Dialog>
    );
  }

  // Boolean / Option types: use SelectDropdown with custom trigger
  if (conditionFilterType === "option" || conditionFilterType === "boolean") {
    const filterChoice = dataConfigFilter?.filter_choices.find(
      (choice) => choice.key === condition.key,
    );
    let options = filterChoice?.options;
    let conditionSelectType = filterChoice?.type;

    if (!options) {
      const parentChoice = dataConfigFilter?.filter_choices
        .filter((choice) => "children" in choice)
        .find((choice) => choice.children?.some((child) => child.key === condition.key));
      if (parentChoice) {
        const childChoice = parentChoice.children?.find(
          (child) => child.key === condition.key,
        );
        options = childChoice?.options;
        conditionSelectType = childChoice?.type;
      }
    }

    if (!options || options.length === 0) {
      return (
        <UniqueLabelsSelect
          condition={condition}
          setTempFilter={(updater) => {
            if (typeof updater === "function") {
              const result = updater(filter);
              if (result) {
                const newVal = result.conditions?.[0]?.value;
                if (newVal !== undefined) updateFilterValue(filter.id, newVal);
              }
            }
          }}
          conditionIndex={0}
          filter={filter}
          customTrigger={
            <button
              className={cn(
                chipValueClass,
                "cursor-pointer hover:bg-[#FCE5D9] transition-colors",
              )}
            >
              {valueDisplay}
            </button>
          }
        />
      );
    }

    if (conditionSelectType === "single_select" || filter.type === "single_select") {
      if (options.length === 2) {
        const currentValue = condition.value as string;
        const otherOption = options.find((o) => String(o.key) !== String(currentValue)) ?? options[0];
        return (
          <button
            className={cn(
              chipValueClass,
              "cursor-pointer hover:bg-[#FCE5D9] transition-colors",
            )}
            onClick={() => updateFilterValue(filter.id, otherOption.key as string)}
          >
            {valueDisplay}
          </button>
        );
      }
      return (
        <SelectDropdown
          options={options.map((option) => ({
            key: option.key,
            label: option.label,
            value: option.key,
          }))}
          showTooltip={true}
          selected={condition.value as string}
          onChange={(newValue: string) => updateFilterValue(filter.id, newValue)}
          placeholder={`Select ${condition?.label}`}
          className="h-7"
          enableSearch={options.length > 5}
          searchPlaceholder="Search"
          sideOffset={7}
          customTrigger={
            <button
              className={cn(
                chipValueClass,
                "cursor-pointer hover:bg-[#FCE5D9] transition-colors",
              )}
            >
              {valueDisplay}
            </button>
          }
        />
      );
    }

    return (
      <SelectDropdown
        multiSelectSaveLabel="Apply"
        multiSelectShowSaveButton
        multiSelectRequired
        options={options.map((option) => ({
          key: option.key,
          label: option.label,
          value: option.key,
          ...(option.platform && {
            icon: () => {
              if (!option?.platform) return null;
              return (
                <img
                  className="h-[14px] w-[14px] flex-shrink-0"
                  src={getPlatformIcon(option.platform)}
                />
              );
            },
          }),
        }))}
        showTooltip={true}
        selected=""
        onChange={() => {}}
        selectedValues={Array.isArray(condition.value) ? condition.value : []}
        onChangeMultiple={(newValues: string[]) =>
          updateFilterValue(filter.id, newValues)
        }
        multiSelect={true}
        placeholder={`Select ${condition?.label}`}
        className="h-7"
        enableSearch={true}
        searchPlaceholder="Search"
        sideOffset={5}
        customTrigger={
          <button
            className={cn(
              chipValueClass,
              "cursor-pointer hover:bg-[#FCE5D9] transition-colors",
            )}
          >
            {valueDisplay}
          </button>
        }
      />
    );
  }

  // Asset types (ad_name, adset_name, campaign_name): chip button that opens asset search popover
  if (["ad_name", "adset_name", "campaign_name"].includes(condition.key || filter.key)) {
    return (
      <InlineAssetMultiValueBlock
        filter={filter}
        condition={condition}
        updateFilterValueAndOperator={updateFilterValueAndOperator}
        removeFilter={removeFilter}
        valueDisplay={valueDisplay}
        chipValueClass={chipValueClass}
      />
    );
  }

  // Number BETWEEN: Popover with min/max inputs
  if (condition.operator === "BETWEEN") {
    const minMaxValue = localValue as { min: number; max: number } | undefined;
    return (
      <Popover>
        <PopoverTrigger asChild>
          <button
            className={cn(
              chipValueClass,
              "cursor-pointer hover:bg-[#FCE5D9] transition-colors",
            )}
          >
            {valueDisplay}
          </button>
        </PopoverTrigger>
        <PopoverContent
          side="bottom"
          align="start"
          className="w-auto p-3 rounded-[8px]"
          onCloseAutoFocus={(e) => e.preventDefault()}
          onInteractOutside={() => {
            if (localValue !== condition.value) {
              updateFilterValue(filter.id, localValue as { min: number; max: number });
            }
          }}
        >
          <div className="flex gap-2 items-center">
            <div className="relative flex items-center">
              {columnOption?.prefix && minMaxValue?.min !== undefined && String(minMaxValue.min) !== "" && (
                <span className="absolute left-[8px] text-[12px] text-[#02185966] pointer-events-none">{columnOption.prefix}</span>
              )}
              <SqInput
                shadow="sm"
                value={minMaxValue?.min !== undefined ? minMaxValue.min : ""}
                onChange={(e) => {
                  const numVal = e.target.value === "" ? 0 : Number(e.target.value);
                  setLocalValue((prev) => {
                    const cur = prev as { min: number; max: number };
                    return { min: numVal, max: cur?.max ?? 0 };
                  });
                }}
                className={cn(
                  "flex w-[90px] items-center gap-[6px] rounded-[8px] overflow-hidden text-brand-primary-new/80 text-ellipsis text-[12px] font-normal leading-[19.157px] tracking-[0.24px] placeholder:text-[#02185966]",
                  columnOption?.prefix && minMaxValue?.min !== undefined && String(minMaxValue.min) !== "" && "pl-[20px]",
                )}
                type="number"
                placeholder="Min"
              />
              {columnOption?.suffix && minMaxValue?.min !== undefined && String(minMaxValue.min) !== "" && (
                <span className={cn("absolute flex items-center pointer-events-none", columnOption?.prefix ? "left-[20px]" : "left-[8px]")}>
                  <span className="text-[12px] font-normal invisible whitespace-pre">{String(minMaxValue.min)}</span>
                  <span className="text-[12px] text-[#02185966] ml-[2px]">{columnOption.suffix}</span>
                </span>
              )}
            </div>
            <div className="relative flex items-center">
              {columnOption?.prefix && minMaxValue?.max !== undefined && String(minMaxValue.max) !== "" && (
                <span className="absolute left-[8px] text-[12px] text-[#02185966] pointer-events-none">{columnOption.prefix}</span>
              )}
              <SqInput
                shadow="sm"
                value={minMaxValue?.max !== undefined ? minMaxValue.max : ""}
                onChange={(e) => {
                  const numVal = e.target.value === "" ? 0 : Number(e.target.value);
                  setLocalValue((prev) => {
                    const cur = prev as { min: number; max: number };
                    return { min: cur?.min ?? 0, max: numVal };
                  });
                }}
                className={cn(
                  "flex w-[90px] items-center gap-[6px] rounded-[8px] overflow-hidden text-brand-primary-new/80 text-ellipsis text-[12px] font-normal leading-[19.157px] tracking-[0.24px] placeholder:text-[#02185966]",
                  columnOption?.prefix && minMaxValue?.max !== undefined && String(minMaxValue.max) !== "" && "pl-[20px]",
                )}
                type="number"
                placeholder="Max"
              />
              {columnOption?.suffix && minMaxValue?.max !== undefined && String(minMaxValue.max) !== "" && (
                <span className={cn("absolute flex items-center pointer-events-none", columnOption?.prefix ? "left-[20px]" : "left-[8px]")}>
                  <span className="text-[12px] font-normal invisible whitespace-pre">{String(minMaxValue.max)}</span>
                  <span className="text-[12px] text-[#02185966] ml-[2px]">{columnOption.suffix}</span>
                </span>
              )}
            </div>
            <SqButton
              variant="default"
              size="md"
              className="text-white border-0 cursor-pointer"
              onClick={() =>
                updateFilterValue(filter.id, localValue as { min: number; max: number })
              }
            >
              Apply
            </SqButton>
          </div>
        </PopoverContent>
      </Popover>
    );
  }

  // Default: string / number input in a Popover
  const [valuePopoverOpen, setValuePopoverOpen] = useState(false);

  return (
    <Popover
      open={valuePopoverOpen}
      onOpenChange={(open) => {
        if (!open) {
          setLocalValue(condition.value);
        }
        setValuePopoverOpen(open);
      }}
    >
      <PopoverTrigger asChild>
        <button
          className={cn(
            chipValueClass,
            "cursor-pointer hover:bg-[#FCE5D9] transition-colors",
          )}
        >
          {valueDisplay}
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="bottom"
        align="start"
        className="w-auto p-[6px] rounded-[12px] border-0 shadow-none squircle squircle-smooth-[1] squircle-radius-[12px] squircle-outline-[1px] squircle-outline-color-border-gray bg-white drop-shadow-md"
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <span className="text-[12px] font-medium text-[#35467A] pl-[4px]">Value</span>
            <SqButton
              variant="default"
              size="sm"
              className="text-white border-0 cursor-pointer squircle-mask"
              onClick={() => {
                updateFilterValue(
                  filter.id,
                  conditionFilterType === "number"
                    ? localValue === ""
                      ? ""
                      : Number(localValue)
                    : localValue,
                );
                setValuePopoverOpen(false);
              }}
            >
              Apply
            </SqButton>
          </div>
          <div className="relative flex items-center">
            {conditionFilterType === "number" && columnOption?.prefix && localValue !== "" && localValue !== undefined && (
              <span className="absolute left-[10px] text-[12px] text-[#02185966] pointer-events-none">
                {columnOption.prefix}
              </span>
            )}
            <SqInput
              shadow="sm"
              autoFocus
              value={
                typeof localValue === "object"
                  ? JSON.stringify(localValue)
                  : localValue !== undefined
                    ? localValue
                    : ""
              }
              onChange={(e) => {
                const inputValue = e.target.value;
                if (conditionFilterType === "number") {
                  if (
                    inputValue === "" ||
                    inputValue === "-" ||
                    /^-?\d*\.?\d*$/.test(inputValue)
                  ) {
                    setLocalValue(inputValue);
                  }
                } else {
                  setLocalValue(inputValue);
                }
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  updateFilterValue(
                    filter.id,
                    conditionFilterType === "number"
                      ? localValue === ""
                        ? ""
                        : Number(localValue)
                      : localValue,
                  );
                  setValuePopoverOpen(false);
                }
              }}
              className={cn(
                "flex w-[220px] items-center gap-[6px] rounded-[8px] overflow-hidden text-[#35467A] text-[12px] font-medium leading-[19.157px] tracking-[0.24px] placeholder:text-[#02185966] placeholder:font-normal px-[8px]",
                conditionFilterType === "number" && columnOption?.prefix && localValue !== "" && localValue !== undefined && "pl-[22px]",
              )}
              type="text"
              inputMode={conditionFilterType === "number" ? "decimal" : "text"}
              placeholder="Enter value"
            />
            {conditionFilterType === "number" && columnOption?.suffix && localValue !== "" && localValue !== undefined && (
              <span className={cn("absolute flex items-center pointer-events-none", columnOption?.prefix ? "left-[22px]" : "left-[10px]")}>
                <span className="text-[12px] font-medium invisible whitespace-pre">{String(localValue)}</span>
                <span className="text-[12px] text-[#02185966] ml-[2px]">{columnOption.suffix}</span>
              </span>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

// Inline asset multi-value block — opens the same AssetSearchSidePanel for editing array values
const InlineAssetMultiValueBlock = ({
  filter,
  condition,
  updateFilterValueAndOperator,
  removeFilter,
  valueDisplay,
  chipValueClass,
}: {
  filter: SectionFilter;
  condition: NonNullable<SectionFilter["conditions"]>[number];
  updateFilterValueAndOperator: (
    filterId: string | undefined,
    newValue: string | string[],
    newOperator: string,
  ) => void;
  removeFilter?: (filter: SectionFilter) => void;
  valueDisplay: string;
  chipValueClass: string;
}) => {
  const [open, setOpen] = useState(false);
  const currentValues = Array.isArray(condition.value)
    ? (condition.value as string[])
    : condition.value && typeof condition.value === "string"
      ? [condition.value as string]
      : [];

  const choice = {
    key: condition.key || filter.key,
    label: condition.label || filter.label || "",
    filter_type: filter.filter_type || "string",
    field: filter.field || "",
  } as NonNullable<DataConfigurations["filter"]["filter_choices"]>[number];

  return (
    <Popover open={open} onOpenChange={(nextOpen) => {
      if (!nextOpen) {
        const v = condition.value;
        const isEmpty = v === "" || v === undefined || (Array.isArray(v) && v.length === 0);
        if (isEmpty) {
          removeFilter?.(filter);
          return;
        }
      }
      setOpen(nextOpen);
    }}>
      <PopoverTrigger asChild>
        <button
          className={cn(
            chipValueClass,
            "cursor-pointer hover:bg-[#FCE5D9] transition-colors",
          )}
        >
          {valueDisplay}
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-auto p-0 border-0 bg-transparent shadow-none"
        align="start"
        sideOffset={6}
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <AssetSearchSidePanel
          choice={choice}
          initialSelectedItems={currentValues}
          onCommit={(value) => {
            const newValues = [...new Set([...currentValues, value])];
            updateFilterValueAndOperator(
              filter.id,
              newValues.length === 1 ? newValues[0] : newValues,
              resolveAssetOp(condition.operator, newValues.length),
            );
          }}
          onRemove={(value) => {
            const remaining = currentValues.filter((v) => v !== value);
            if (remaining.length === 0) {
              updateFilterValueAndOperator(filter.id, "", resolveAssetOp(condition.operator, 0));
            } else {
              updateFilterValueAndOperator(
                filter.id,
                remaining.length === 1 ? remaining[0] : remaining,
                resolveAssetOp(condition.operator, remaining.length),
              );
            }
          }}
          onEdit={(oldValue, newValue) => {
            const newValues = currentValues.map((v) => (v === oldValue ? newValue : v));
            const deduped = [...new Set(newValues)];
            updateFilterValueAndOperator(
              filter.id,
              deduped.length === 1 ? deduped[0] : deduped,
              resolveAssetOp(condition.operator, deduped.length),
            );
          }}
          onClose={() => setOpen(false)}
        />
      </PopoverContent>
    </Popover>
  );
};

// Inline asset value block - chip-styled trigger that opens original asset search popover
const InlineAssetValueBlock = ({
  filter,
  condition,
  updateFilterValue,
  valueDisplay,
  chipValueClass,
}: {
  filter: SectionFilter;
  condition: NonNullable<SectionFilter["conditions"]>[number];
  updateFilterValue: (
    filterId: string | undefined,
    newValue:
      | string
      | string[]
      | number
      | { min: number; max: number }
      | { start_date: string; end_date: string; label: TimeInterval },
  ) => void;
  valueDisplay: string;
  chipValueClass: string;
}) => {
  const { accountId } = useParams();
  const assetType = (condition.key || filter.key)?.split("_")[0];
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState(
    typeof condition.value === "string" ? condition.value : "",
  );
  const [assets, setAssets] = useState<string[]>([]);
  const [isBackendLoading, setIsBackendLoading] = useState(true);
  const fetchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const commitTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (typeof condition.value === "string") {
      setSearchTerm(condition.value);
    }
  }, [condition.value]);

  useEffect(() => {
    if (!open || !accountId) return;

    const trimmed = searchTerm.trim();

    const fullList = assetCache.get(getCacheKey(accountId, assetType, ""));
    const cacheFiltered = fullList
      ? fullList.filter((item) => item.toLowerCase().includes(trimmed.toLowerCase()))
      : [];
    setAssets(cacheFiltered);

    const controller = new AbortController();
    setIsBackendLoading(true);

    const runFetch = () => {
      (async () => {
        try {
          const results = await fetchAssetsWithCache(accountId, assetType, trimmed, {
            signal: controller.signal,
          });
          setAssets((prev) => [...prev, ...results.filter((r) => !prev.includes(r))]);
        } catch {
          /* cancelled */
        } finally {
          setIsBackendLoading(false);
        }
      })();
    };

    if (trimmed === "") {
      runFetch();
    } else {
      if (fetchTimeoutRef.current) clearTimeout(fetchTimeoutRef.current);
      fetchTimeoutRef.current = setTimeout(runFetch, 300);
    }

    return () => {
      controller.abort();
      if (fetchTimeoutRef.current) clearTimeout(fetchTimeoutRef.current);
    };
  }, [searchTerm, accountId, assetType, open]);

  // Debounced filter value commit (500ms after user stops typing)
  useEffect(() => {
    if (commitTimeoutRef.current) clearTimeout(commitTimeoutRef.current);
    commitTimeoutRef.current = setTimeout(() => {
      if (searchTerm !== condition.value) {
        updateFilterValue(filter.id, searchTerm);
      }
    }, 500);
    return () => {
      if (commitTimeoutRef.current) clearTimeout(commitTimeoutRef.current);
    };
  }, [searchTerm]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectAsset = (value: string) => {
    setSearchTerm(value);
    updateFilterValue(filter.id, value);
    setOpen(false);
  };

  return (
    <Popover
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen && searchTerm !== condition.value) {
          updateFilterValue(filter.id, searchTerm);
        }
        setOpen(isOpen);
      }}
    >
      <PopoverTrigger asChild>
        <button
          className={cn(
            chipValueClass,
            "cursor-pointer hover:bg-[#FCE5D9] transition-colors",
          )}
        >
          {valueDisplay}
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="flex flex-col w-72 p-0 min-h-[240px] max-h-[360px] overflow-y-auto overflow-x-hidden break-all bg-[#FFF] rounded-[8px]"
        onOpenAutoFocus={(e) => e.preventDefault()}
        align="start"
        sideOffset={6}
      >
        <div className="p-2">
          <SqInput
            shadow="sm"
            autoFocus
            className="h-8 w-full text-[12px] text-brand-primary-new/80 rounded-[8px] placeholder:text-[#02185966]"
            type="text"
            placeholder={`Enter ${assetType} name`}
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                selectAsset(searchTerm);
              }
            }}
          />
        </div>
        <div
          className={`text-[12px] ${searchTerm ? "!text-[#02185999] cursor-pointer" : "!text-[rgba(2,24,89,0.40)]"} px-2 pb-2 flex gap-1`}
          onClick={() => searchTerm && selectAsset(searchTerm)}
        >
          <TextSelect className="h-[16px] w-[16px] shrink-0" />
          <div>
            <span>Select all results that contain&nbsp;</span>
            <span className="text-[12px] text-brand-primary-new">{searchTerm}</span>
          </div>
        </div>
        <Separator />
        <div className="flex flex-col flex-grow">
          {assets.length > 0 ? (
            assets.map((asset_name) => (
              <div
                className="text-[12px] !text-brand-primary-800 p-2 cursor-pointer hover:bg-[#F6F6F6]"
                key={asset_name}
                onClick={() => selectAsset(asset_name)}
              >
                {asset_name}
              </div>
            ))
          ) : isBackendLoading ? (
            <div className="my-auto p-2">
              <Shimmer initialRows={7} maxRows={7} />
            </div>
          ) : (
            <div className="h-full text-[12px] w-[100%] text-center my-auto text-[#021859B2] py-4">
              No results found
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

// Hook for safe-triangle submenu intent detection (atan2 approach)
// Shared submenu intent constants and logic
const SUBMENU_ANGLE_TOLERANCE = (15 * Math.PI) / 180;
const SUBMENU_IDLE_TIMEOUT = 300;

function isMovingTowardSubmenu(
  prevX: number,
  prevY: number,
  currX: number,
  currY: number,
  submenuRef: React.RefObject<HTMLDivElement | null>,
  submenuSide: "left" | "right",
): boolean {
  const el = submenuRef.current;
  if (!el) return false;
  const rect = el.getBoundingClientRect();
  const dx = currX - prevX;
  const dy = currY - prevY;
  const mx = submenuSide === "left" ? -1 : 1;
  const adjDx = mx * dx;
  if (adjDx <= 0) return false;
  const adjPrevX = mx * prevX;
  const targetX = mx * (submenuSide === "right" ? rect.left : rect.right);
  const pointerAngle = Math.atan2(dy, adjDx);
  const angleToTop = Math.atan2(rect.top - prevY, targetX - adjPrevX);
  const angleToBottom = Math.atan2(rect.bottom - prevY, targetX - adjPrevX);
  const minAngle = Math.min(angleToTop, angleToBottom) - SUBMENU_ANGLE_TOLERANCE;
  const maxAngle = Math.max(angleToTop, angleToBottom) + SUBMENU_ANGLE_TOLERANCE;
  return pointerAngle >= minAngle && pointerAngle <= maxAngle;
}

const useSubMenuIntent = ({
  containerRef,
  submenuRef,
  activeKey,
  submenuSide = "right",
}: {
  containerRef: React.RefObject<HTMLDivElement | null>;
  submenuRef: React.RefObject<HTMLDivElement | null>;
  activeKey: string | null;
  submenuSide?: "left" | "right";
}) => {
  const pendingKeyRef = useRef<{
    key: string | null;
    parentKey: string | null;
    top: number;
    hasSidePanel: boolean;
  } | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const invalidCountRef = useRef(0);
  const lastPointerRef = useRef<{ x: number; y: number } | null>(null);

  const clearPending = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    pendingKeyRef.current = null;
    invalidCountRef.current = 0;
  }, []);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  useEffect(() => {
    if (!activeKey) {
      clearPending();
      lastPointerRef.current = null;
    }
  }, [activeKey, clearPending]);

  const isMovingTowardSubmenuCb = useCallback(
    (prevX: number, prevY: number, currX: number, currY: number) =>
      isMovingTowardSubmenu(prevX, prevY, currX, currY, submenuRef, submenuSide),
    [submenuRef, submenuSide],
  );

  const applyPending = useCallback(
    (
      setActiveAssetKey: (key: string | null) => void,
      setActiveParentKey: (key: string | null) => void,
      setSidePanelTop: (top: number) => void,
    ) => {
      const pending = pendingKeyRef.current;
      if (!pending) return;
      if (pending.hasSidePanel) {
        setActiveAssetKey(pending.key);
        setActiveParentKey(pending.parentKey);
        setSidePanelTop(pending.top);
      } else {
        setActiveAssetKey(null);
        setActiveParentKey(null);
      }
      clearPending();
    },
    [clearPending],
  );

  const scheduleSwitch = useCallback(
    (
      setActiveAssetKey: (key: string | null) => void,
      setActiveParentKey: (key: string | null) => void,
      setSidePanelTop: (top: number) => void,
    ) => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        applyPending(setActiveAssetKey, setActiveParentKey, setSidePanelTop);
      }, SUBMENU_IDLE_TIMEOUT);
    },
    [applyPending],
  );

  const handleItemMouseEnter = useCallback(
    (
      e: React.MouseEvent,
      itemKey: string | null,
      parentKey: string | null,
      hasSidePanel: boolean,
      setActiveAssetKey: (key: string | null) => void,
      setActiveParentKey: (key: string | null) => void,
      setSidePanelTop: (top: number) => void,
    ) => {
      if (itemKey === activeKey) {
        clearPending();
        return;
      }

      let top = 0;
      if (hasSidePanel && containerRef.current) {
        const containerRect = containerRef.current.getBoundingClientRect();
        const itemRect = (e.currentTarget as HTMLElement).getBoundingClientRect();
        top = itemRect.top - containerRect.top;
      }

      if (!activeKey || !submenuRef.current) {
        // No active submenu, switch immediately
        if (hasSidePanel) {
          setActiveAssetKey(itemKey);
          setActiveParentKey(parentKey);
          setSidePanelTop(top);
        } else {
          setActiveAssetKey(null);
          setActiveParentKey(null);
        }
        clearPending();
        return;
      }

      // Active submenu exists — queue the switch and let pointermove decide
      pendingKeyRef.current = { key: itemKey, parentKey, top, hasSidePanel };
      invalidCountRef.current = 0;
      scheduleSwitch(setActiveAssetKey, setActiveParentKey, setSidePanelTop);
    },
    [activeKey, containerRef, submenuRef, clearPending, scheduleSwitch],
  );

  const handlePointerMove = useCallback(
    (
      e: PointerEvent,
      setActiveAssetKey: (key: string | null) => void,
      setActiveParentKey: (key: string | null) => void,
      setSidePanelTop: (top: number) => void,
    ) => {
      if (e.pointerType !== "mouse") return;

      // If cursor is inside the submenu, cancel any pending switch
      const submenuEl = submenuRef.current;
      if (submenuEl) {
        const rect = submenuEl.getBoundingClientRect();
        if (
          e.clientX >= rect.left &&
          e.clientX <= rect.right &&
          e.clientY >= rect.top &&
          e.clientY <= rect.bottom
        ) {
          clearPending();
          lastPointerRef.current = { x: e.clientX, y: e.clientY };
          return;
        }
      }

      if (!pendingKeyRef.current || !activeKey) {
        lastPointerRef.current = { x: e.clientX, y: e.clientY };
        return;
      }

      const prev = lastPointerRef.current;
      lastPointerRef.current = { x: e.clientX, y: e.clientY };
      if (!prev) return;

      if (isMovingTowardSubmenuCb(prev.x, prev.y, e.clientX, e.clientY)) {
        invalidCountRef.current = 0;
        scheduleSwitch(setActiveAssetKey, setActiveParentKey, setSidePanelTop);
      } else {
        invalidCountRef.current++;
        if (invalidCountRef.current >= 2) {
          applyPending(setActiveAssetKey, setActiveParentKey, setSidePanelTop);
        }
      }
    },
    [activeKey, isMovingTowardSubmenuCb, scheduleSwitch, applyPending],
  );

  return { handleItemMouseEnter, handlePointerMove, clearPending };
};

// Lightweight safe-triangle hook for simple submenu panels (e.g. OperatorSidePanel)
const useSimpleSubMenuIntent = ({
  containerRef,
  submenuRef,
  activeKey,
  setActiveKey,
  submenuSide = "right",
}: {
  containerRef: React.RefObject<HTMLDivElement | null>;
  submenuRef: React.RefObject<HTMLDivElement | null>;
  activeKey: string | null;
  setActiveKey: (key: string | null) => void;
  submenuSide?: "left" | "right";
}) => {
  const pendingRef = useRef<string | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const invalidCountRef = useRef(0);
  const lastPointerRef = useRef<{ x: number; y: number } | null>(null);

  const clearPending = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    pendingRef.current = null;
    invalidCountRef.current = 0;
  }, []);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  useEffect(() => {
    if (!activeKey) {
      clearPending();
      lastPointerRef.current = null;
    }
  }, [activeKey, clearPending]);

  const isMovingToward = useCallback(
    (prevX: number, prevY: number, currX: number, currY: number) =>
      isMovingTowardSubmenu(prevX, prevY, currX, currY, submenuRef, submenuSide),
    [submenuRef, submenuSide],
  );

  const applyPending = useCallback(() => {
    setActiveKey(pendingRef.current);
    clearPending();
  }, [setActiveKey, clearPending]);

  const scheduleSwitch = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(applyPending, SUBMENU_IDLE_TIMEOUT);
  }, [applyPending]);

  const requestHover = useCallback(
    (key: string | null) => {
      if (key === activeKey) {
        clearPending();
        return;
      }
      if (!activeKey || !submenuRef.current) {
        setActiveKey(key);
        clearPending();
        return;
      }
      pendingRef.current = key;
      invalidCountRef.current = 0;
      scheduleSwitch();
    },
    [activeKey, submenuRef, setActiveKey, clearPending, scheduleSwitch],
  );

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const handler = (e: PointerEvent) => {
      if (e.pointerType !== "mouse") return;
      const el = submenuRef.current;
      if (el) {
        const rect = el.getBoundingClientRect();
        if (
          e.clientX >= rect.left &&
          e.clientX <= rect.right &&
          e.clientY >= rect.top &&
          e.clientY <= rect.bottom
        ) {
          clearPending();
          lastPointerRef.current = { x: e.clientX, y: e.clientY };
          return;
        }
      }
      if (pendingRef.current === null && activeKey === null) {
        lastPointerRef.current = { x: e.clientX, y: e.clientY };
        return;
      }
      if (pendingRef.current === null) {
        lastPointerRef.current = { x: e.clientX, y: e.clientY };
        return;
      }
      const prev = lastPointerRef.current;
      lastPointerRef.current = { x: e.clientX, y: e.clientY };
      if (!prev) return;
      if (isMovingToward(prev.x, prev.y, e.clientX, e.clientY)) {
        invalidCountRef.current = 0;
        scheduleSwitch();
      } else {
        invalidCountRef.current++;
        if (invalidCountRef.current >= 2) {
          applyPending();
        }
      }
    };
    container.addEventListener("pointermove", handler);
    return () => container.removeEventListener("pointermove", handler);
  }, [
    containerRef,
    submenuRef,
    activeKey,
    isMovingToward,
    scheduleSwitch,
    applyPending,
    clearPending,
  ]);

  return { requestHover };
};

function useSubmenuBottomClamp(
  submenuRef: React.RefObject<HTMLDivElement | null>,
  dep: unknown,
) {
  useLayoutEffect(() => {
    const sub = submenuRef.current;
    if (!sub) return;
    const subRect = sub.getBoundingClientRect();
    if (subRect.bottom > window.innerHeight - 12) {
      const offset = subRect.bottom - (window.innerHeight - 12);
      const currentTop = parseFloat(sub.style.top) || 0;
      sub.style.top = `${currentTop - offset}px`;
    }
  }, [submenuRef, dep]);
}

// Common filter dropdown component
const FilterDropdown = ({
  searchTerm,
  setSearchTerm,
  dataConfigFilter,
  expandedChoices,
  setExpandedChoices,
  handleFilterClick,
  onAssetFilterCommit,
  onAssetFilterRemoveValue,
  onAdvancedFiltersClick,
  activeAssetKey,
  setActiveAssetKey,
  clearSelection,
  selectedAccount,
  allowedDateRange,
  columnOptionMap,
}: {
  searchTerm: string;
  setSearchTerm: (value: string) => void;
  dataConfigFilter: DataConfigurationsFilter | null;
  expandedChoices: Record<string, boolean>;
  setExpandedChoices: (
    value:
      | Record<string, boolean>
      | ((prev: Record<string, boolean>) => Record<string, boolean>),
  ) => void;
  handleFilterClick: (
    item: NonNullable<DataConfigurations["filter"]["filter_choices"]>[number],
    child?: NonNullable<(typeof item)["children"]>[number],
  ) => void;
  onAssetFilterCommit?: (
    item: NonNullable<DataConfigurations["filter"]["filter_choices"]>[number],
    value: string | string[],
    parentKey?: string,
    operatorKey?: string,
    existingFilterId?: string,
  ) => string | undefined | void;
  onAssetFilterRemoveValue?: (
    item: NonNullable<DataConfigurations["filter"]["filter_choices"]>[number],
    value: string,
  ) => void;
  onAdvancedFiltersClick?: () => void;
  activeAssetKey?: string | null;
  setActiveAssetKey?: (key: string | null) => void;
  clearSelection?: () => void;
  selectedAccount?: { time_zone?: string } | null;
  allowedDateRange?: { first_allowed_date?: Date; last_allowed_date?: Date } | null;
  columnOptionMap?: Map<string, ColumnOption> | null;
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const submenuRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [sidePanelTop, setSidePanelTop] = useState(0);
  const [activeParentKey, setActiveParentKey] = useState<string | null>(null);
  const [submenuSide, setSubmenuSide] = useState<"left" | "right">("right");
  const [scrollMaxH, setScrollMaxH] = useState(585);

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const spaceRight = window.innerWidth - rect.right;
    setSubmenuSide(spaceRight < 320 ? "left" : "right");
  }, [activeAssetKey]);

  useEffect(() => {
    const scrollEl = scrollRef.current;
    if (!scrollEl) return;

    const recalc = () => {
      const rect = scrollEl.getBoundingClientRect();
      const topEdge = Math.max(12, rect.top);
      const available = window.innerHeight - topEdge - 12;
      setScrollMaxH(Math.min(585, Math.max(120, available)));
    };

    // Delay to let Radix position the dropdown first
    const raf = requestAnimationFrame(() => {
      recalc();
    });

    const ro = new ResizeObserver(recalc);
    const parent = scrollEl.closest("[data-radix-popper-content-wrapper]");
    if (parent) ro.observe(parent);

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, []);

  useLayoutEffect(() => {
    const sub = submenuRef.current;
    const container = containerRef.current;
    if (!sub || !container) return;

    const clampBottom = () => {
      sub.style.top = `${sidePanelTop}px`;
      const containerTop = container.getBoundingClientRect().top;
      const subHeight = sub.offsetHeight;
      const bottomEdge = containerTop + sidePanelTop + subHeight;
      const viewportH = window.innerHeight;
      if (bottomEdge > viewportH - 12) {
        const adjusted = viewportH - 12 - subHeight - containerTop;
        sub.style.top = `${Math.max(0, adjusted)}px`;
      }
    };

    clampBottom();

    const ro = new ResizeObserver(clampBottom);
    ro.observe(sub);
    return () => ro.disconnect();
  }, [activeAssetKey, sidePanelTop]);

  const { handleItemMouseEnter, handlePointerMove } = useSubMenuIntent({
    containerRef,
    submenuRef,
    activeKey: activeAssetKey ?? null,
    submenuSide,
  });

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const handler = (e: PointerEvent) => {
      handlePointerMove(
        e,
        setActiveAssetKey ?? (() => {}),
        setActiveParentKey,
        setSidePanelTop,
      );
    };
    container.addEventListener("pointermove", handler);
    return () => container.removeEventListener("pointermove", handler);
  }, [handlePointerMove, setActiveAssetKey]);

  const activeAssetChoice = (() => {
    if (!activeAssetKey) return null;
    const regular = dataConfigFilter?.filter_choices.find(
      (c) => c.key === activeAssetKey,
    );
    if (regular) return regular;
    for (const choice of dataConfigFilter?.filter_choices || []) {
      if ("children" in choice && choice.children) {
        const child = choice.children.find((c) => c.key === activeAssetKey);
        if (child) return child;
      }
    }
    return null;
  })();

  const choiceHasSidePanel = (choice: {
    key: string;
    label?: string;
    filter_type: string;
    options?: unknown[];
  }) => {
    if (ASSET_FILTER_KEYS.includes(choice.key)) return true;
    if (isDaysFromLaunch(choice)) return true;
    if (isAdStarted(choice)) return true;
    if (METRIC_SIDE_PANEL_TYPES.includes(choice.filter_type)) return true;
    if (EXCLUDED_SIDE_PANEL_TYPES.includes(choice.filter_type)) return false;
    if (choice.filter_type === "option" || choice.filter_type === "boolean") return true;
    if (choice.options && choice.options.length > 0) return true;
    return false;
  };
  // Check if there are any results
  const hasRegularChoices =
    (dataConfigFilter?.filter_choices || [])
      .filter((choice) => !("children" in choice))
      .filter((c) => c.label.toLowerCase().includes(searchTerm.toLowerCase())).length > 0;

  const hasChildChoices = (dataConfigFilter?.filter_choices || [])
    .filter((choice) => "children" in choice)
    .some(
      (choice) =>
        choice?.children &&
        choice?.children.filter((c) =>
          c.label.toLowerCase().includes(searchTerm.toLowerCase()),
        )?.length > 0,
    );

  const hasResults = hasRegularChoices || hasChildChoices;

  return (
    <div className="relative" ref={containerRef}>
      <SqCommand className={"w-[230px] rounded-[12px]"} radius={12} shouldFilter={false}>
        <SqCommandInput
          placeholder="Search"
          className="mb-0 text-[12px]"
          wrapperClass="px-2"
          onValueChange={(value) => setSearchTerm(value)}
          onFocus={() => {
            setActiveAssetKey?.(null);
            setActiveParentKey(null);
          }}
        />

        {/* Empty state when no results found */}
        {searchTerm.trim() && !hasResults && <SearchEmptyState />}

        <div
          ref={scrollRef}
          className="overflow-y-auto mb-[2px] mt-1"
          style={{ maxHeight: scrollMaxH }}
        >
          {!searchTerm.trim() && onAdvancedFiltersClick && (
            <>
              <SqCommandGroup className="pb-0">
                <SqCommandItem
                  value="Advanced filters"
                  className="rounded-[8px] flex items-center gap-2 cursor-pointer"
                  onSelect={() => {
                    onAdvancedFiltersClick();
                  }}
                  onMouseEnter={(e) => {
                    handleItemMouseEnter(
                      e,
                      null,
                      null,
                      false,
                      setActiveAssetKey ?? (() => {}),
                      setActiveParentKey,
                      setSidePanelTop,
                    );
                  }}
                >
                  <RiNodeTree size={14} className="text-[#02185966] shrink-0" />
                  <span className="text-brand-primary-new/80 text-[12px] font-normal leading-[20px] pt-[2px]">
                    Advanced filters
                  </span>
                </SqCommandItem>
              </SqCommandGroup>
              <div className="mx-[6px] h-[1px] rounded-full bg-[#EDEDED]" />
            </>
          )}
          <SqCommandGroup className="pb-0">
            <SqTooltipProvider>
              {searchSort(
                dataConfigFilter?.filter_choices
                  .filter((choice) => !("children" in choice))
                  .filter((c) => c.label.toLowerCase().includes(searchTerm.toLowerCase())) || [],
                searchTerm,
                (c) => c.label,
              ).map((choice) => (
                  <SqTooltip key={choice.key}>
                    <SqCommandItem
                      value={choice.label}
                      hoverBgColor={
                        activeAssetKey && activeAssetKey !== choice.key
                          ? "transparent"
                          : undefined
                      }
                      className={cn(
                        "rounded-[8px] flex items-center justify-between",
                        choiceHasSidePanel(choice) ? "cursor-default" : "cursor-pointer",
                        activeAssetKey === choice.key && "squircle-fill-hover-gray",
                      )}
                      onSelect={() => {
                        if (choiceHasSidePanel(choice)) {
                          setActiveAssetKey?.(
                            activeAssetKey === choice.key ? null : choice.key,
                          );
                        } else {
                          handleFilterClick(choice);
                        }
                      }}
                      onMouseEnter={(e) => {
                        handleItemMouseEnter(
                          e,
                          choiceHasSidePanel(choice) ? choice.key : null,
                          null,
                          choiceHasSidePanel(choice),
                          setActiveAssetKey ?? (() => {}),
                          setActiveParentKey,
                          setSidePanelTop,
                        );
                      }}
                    >
                      <SqTooltipTrigger asChild>
                        <div className="flex w-full">
                          <div className="flex items-center gap-2 w-full">
                            <span className={MENU_TEXT_CLS}>
                              {choice.label}
                              {choice.show_platforms && (
                                <span className="text-[#FF4500]">*</span>
                              )}
                            </span>
                          </div>

                          {choice.show_platforms &&
                            choice?.platforms &&
                            choice.platforms.length > 0 && (
                              <PlatformIcons platforms={choice.platforms} />
                            )}
                        </div>
                      </SqTooltipTrigger>
                    </SqCommandItem>
                    {choice.show_platforms &&
                      choice?.platforms &&
                      choice.platforms.length > 0 && (
                        <SqTooltipContent
                          portal={true}
                          side="top"
                          align="start"
                          alignOffset={20}
                          className="z-50"
                        >
                          <PlatformAvailabilityText platforms={choice.platforms} />
                        </SqTooltipContent>
                      )}
                  </SqTooltip>
                ))}
            </SqTooltipProvider>
          </SqCommandGroup>

          {dataConfigFilter?.filter_choices
            .filter(
              (choice) =>
                "children" in choice &&
                choice?.children?.some((c) =>
                  c.label.toLowerCase().includes(searchTerm.toLowerCase()),
                ),
            )
            .map((choice, index) => (
              <Fragment key={choice.key}>
                {choice?.children &&
                  choice?.children.filter((c) =>
                    c.label.toLowerCase().includes(searchTerm.toLowerCase()),
                  )?.length > 0 && (
                    <>
                      {(index > 0 || hasRegularChoices) && (
                        <div className="mx-[6px] my-[4px] h-[1px] rounded-full bg-[#EDEDED]" />
                      )}
                      <div
                        className="px-1 flex justify-between items-center"
                        onMouseEnter={(e) => {
                          handleItemMouseEnter(
                            e,
                            null,
                            null,
                            false,
                            setActiveAssetKey ?? (() => {}),
                            setActiveParentKey,
                            setSidePanelTop,
                          );
                        }}
                      >
                        <SqButton
                          size={"md"}
                          bgColor="white"
                          onClick={() =>
                            setExpandedChoices((prev) => ({
                              ...prev,
                              [choice.key]: !prev[choice.key],
                            }))
                          }
                          shadow={"none"}
                          className="w-full px-2"
                        >
                          <div className="w-full flex justify-between items-center">
                            <span
                              className={`text-xs font-medium leading-[20px] ${
                                expandedChoices[choice.key]
                                  ? "text-[#02185966]"
                                  : "text-[#021859CC]"
                              }`}
                            >
                              {choice.label}
                            </span>
                            <LuChevronDown
                              className={`h-4 w-4 text-brand-primary-new/40 transition-transform duration-200 ${
                                expandedChoices[choice.key] ? "" : "-rotate-90"
                              }`}
                            />
                          </div>
                        </SqButton>
                      </div>

                      {expandedChoices[choice.key] && (
                        <SqCommandGroup className={cn(clearSelection && "mb-0")}>
                          <SqTooltipProvider>
                            {choice.children &&
                              searchSort(
                                sortMetricItems(choice.children)
                                  .filter((c) =>
                                    c.label
                                      .toLowerCase()
                                      .includes(searchTerm.toLowerCase()),
                                  ),
                                searchTerm,
                                (c) => c.label,
                              ).map((child) => (
                                  <SqTooltip key={child.key}>
                                    <SqCommandItem
                                      value={child.label}
                                      hoverBgColor={
                                        activeAssetKey && activeAssetKey !== child.key
                                          ? "transparent"
                                          : undefined
                                      }
                                      className={cn(
                                        "rounded-[8px] flex items-center justify-between",
                                        choiceHasSidePanel(child)
                                          ? "cursor-default"
                                          : "cursor-pointer",
                                        activeAssetKey === child.key &&
                                          "squircle-fill-hover-gray",
                                      )}
                                      onSelect={() => {
                                        if (choiceHasSidePanel(child)) {
                                          setActiveAssetKey?.(
                                            activeAssetKey === child.key
                                              ? null
                                              : child.key,
                                          );
                                          setActiveParentKey(choice.key);
                                        } else {
                                          handleFilterClick(choice, child);
                                        }
                                      }}
                                      onMouseEnter={(e) => {
                                        handleItemMouseEnter(
                                          e,
                                          choiceHasSidePanel(child) ? child.key : null,
                                          choiceHasSidePanel(child) ? choice.key : null,
                                          choiceHasSidePanel(child),
                                          setActiveAssetKey ?? (() => {}),
                                          setActiveParentKey,
                                          setSidePanelTop,
                                        );
                                      }}
                                    >
                                      <SqTooltipTrigger asChild>
                                        <div className="flex w-full gap-1">
                                          <div className="flex items-center gap-0 w-full">
                                            {"creative_type" in child ? (
                                              <MediaIcon
                                                size={14}
                                                variant="raw"
                                                type={child.creative_type ?? "mixed"}
                                                className="mr-2"
                                              />
                                            ) : child?.sub_type === "EXTERNAL" ? (
                                              <IconDatabase className="h-4 w-4 text-[#02185999] mr-1 relative -top-[1px] shrink-0" />
                                            ) : null}
                                            <span className="text-brand-primary-new/80 text-[12px] font-normal leading-[20px] translate-y-[1px]">
                                              {child.label}
                                              {child.show_platforms && (
                                                <span className="text-[#FF4500]">*</span>
                                              )}
                                            </span>
                                          </div>

                                          {child.show_platforms &&
                                            child?.platforms &&
                                            child.platforms.length > 0 && (
                                              <PlatformIcons
                                                platforms={child.platforms}
                                              />
                                            )}
                                        </div>
                                      </SqTooltipTrigger>
                                    </SqCommandItem>
                                    {child.show_platforms &&
                                      child?.platforms &&
                                      child.platforms.length > 0 && (
                                        <SqTooltipContent
                                          portal={true}
                                          side="top"
                                          align="start"
                                          alignOffset={20}
                                          className="z-50"
                                        >
                                          <PlatformAvailabilityText
                                            platforms={child.platforms}
                                          />
                                        </SqTooltipContent>
                                      )}
                                  </SqTooltip>
                                ))}
                          </SqTooltipProvider>
                        </SqCommandGroup>
                      )}
                    </>
                  )}
              </Fragment>
            ))}
        </div>

        {clearSelection && (
          <SqButton
            size={"sm"}
            bgColor="#E321240F"
            hoverBgColor="#E321241F"
            onClick={clearSelection}
            className="border-0 text-[#E32124] justify-start m-1 gap-1 px-2"
          >
            <RiDeleteBackLine size={14} color="#E32124" />
            <span className="translate-y-[1px]"> Clear Selection</span>
          </SqButton>
        )}
      </SqCommand>
      {activeAssetChoice &&
        onAssetFilterCommit &&
        (() => {
          const choice = activeAssetChoice as NonNullable<
            DataConfigurationsFilter["filter_choices"]
          >[number];
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const opts = (activeAssetChoice.options || []) as Array<{
            key: string;
            label: string;
            platform?: any;
          }>;
          return (
            <div
              ref={submenuRef}
              className={cn(
                "absolute z-50",
                submenuSide === "right" ? "left-full ml-1" : "right-full mr-1",
              )}
              style={{ top: sidePanelTop }}
            >
              {ASSET_FILTER_KEYS.includes(choice.key) ? (
                <AssetSearchSidePanel
                  key={choice.key}
                  choice={choice}
                  onCommit={(value, filterId) =>
                    onAssetFilterCommit?.(choice, value, activeParentKey ?? undefined, undefined, filterId)
                  }
                  onRemove={(value) => onAssetFilterRemoveValue?.(choice, value)}
                  onClose={() => setActiveAssetKey?.(null)}
                />
              ) : isDaysFromLaunch(choice) ? (
                <DaysFromLaunchSidePanel
                  key={choice.key}
                  submenuSide={submenuSide}
                  onCommit={(value) => {
                    const operatorKey =
                      dataConfigFilter?.filter_types.find(
                        (t) => t.type === choice.filter_type,
                      )?.operators[0]?.key ?? "";
                    onAssetFilterCommit(
                      choice,
                      value as unknown as string,
                      activeParentKey ?? undefined,
                      operatorKey,
                    );
                  }}
                />
              ) : isAdStarted(choice) ? (
                <AdStartedSidePanel
                  key={choice.key}
                  selectedAccount={selectedAccount || null}
                  allowedDateRange={allowedDateRange || null}
                  onCommit={(value, operatorKey) => {
                    onAssetFilterCommit(
                      choice,
                      value as unknown as string,
                      activeParentKey ?? undefined,
                      operatorKey,
                    );
                  }}
                />
              ) : METRIC_SIDE_PANEL_TYPES.includes(choice.filter_type) ? (
                <OperatorSidePanel
                  key={choice.key}
                  submenuSide={submenuSide}
                  operators={
                    (dataConfigFilter?.filter_types.find(
                      (t) => t.type === choice.filter_type,
                    )?.operators || []).filter((op) => op.key !== "BETWEEN")
                  }
                  onSelect={(operatorKey, value) => {
                    onAssetFilterCommit(
                      choice,
                      value as unknown as string,
                      activeParentKey ?? undefined,
                      operatorKey,
                    );
                  }}
                  prefix={columnOptionMap?.get(choice.key)?.prefix || undefined}
                  suffix={columnOptionMap?.get(choice.key)?.suffix || undefined}
                />
              ) : (
                (() => {
                  const isMultiSelect = choice.type === "multi_select";
                  const enableSearch = opts.length > 5;
                  const formattedOptions = opts.map((opt) => ({
                    key: opt.key,
                    label: opt.label,
                    value: opt.key,
                    ...(opt.platform && {
                      icon: () => {
                        if (!opt.platform) return null;
                        return (
                          <img
                            className="h-[14px] w-[14px] flex-shrink-0"
                            src={getPlatformIcon(opt.platform)}
                          />
                        );
                      },
                    }),
                  }));
                  if (isMultiSelect) {
                    return (
                      <SelectDropdown
                        key={choice.key}
                        renderContentOnly
                        multiSelect
                        multiSelectShowSaveButton
                        multiSelectSaveLabel="Apply"
                        options={formattedOptions}
                        selected=""
                        onChange={() => {}}
                        selectedValues={[]}
                        onChangeMultiple={(values) =>
                          onAssetFilterCommit(
                            choice,
                            values as string[],
                            activeParentKey ?? undefined,
                          )
                        }
                        enableSearch={enableSearch}
                        searchPlaceholder="Search"
                        contentClassName="min-w-[260px]"
                      />
                    );
                  }
                  return (
                    <SelectDropdown
                      key={choice.key}
                      renderContentOnly
                      options={formattedOptions}
                      selected=""
                      onChange={(value) =>
                        onAssetFilterCommit(
                          choice,
                          value as string,
                          activeParentKey ?? undefined,
                        )
                      }
                      enableSearch={enableSearch}
                      searchPlaceholder="Search"
                    />
                  );
                })()
              )}
            </div>
          );
        })()}
    </div>
  );
};

const DaysFromLaunchSidePanel = ({
  onCommit,
  submenuSide = "right",
}: {
  onCommit: (value: number) => void;
  submenuSide?: "left" | "right";
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const submenuRef = useRef<HTMLDivElement>(null);
  const [hoveredCustom, setHoveredCustom] = useState<string | null>(null);
  const [customValue, setCustomValue] = useState("");

  const { requestHover } = useSimpleSubMenuIntent({
    containerRef,
    submenuRef,
    activeKey: hoveredCustom,
    setActiveKey: setHoveredCustom,
    submenuSide,
  });

  useSubmenuBottomClamp(submenuRef, hoveredCustom);

  useEffect(() => {
    setCustomValue("");
  }, [hoveredCustom]);

  return (
    <div
      ref={containerRef}
      className={SQUIRCLE_PANEL_P1_160}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {DAYS_FROM_LAUNCH_PRESETS.map((preset) => (
        <div
          key={preset.value}
          onClick={() => onCommit(preset.value)}
          onMouseEnter={() => requestHover(null)}
          className={MENU_ITEM_CLS}
        >
          <span className={MENU_TEXT_CLS}>{preset.label}</span>
        </div>
      ))}
      <div className="relative" onMouseEnter={() => requestHover("custom")}>
        <div className={cn(MENU_ITEM_CLS, hoveredCustom === "custom" && "bg-hover-gray")}>
          <span className={MENU_TEXT_CLS}>Custom</span>
        </div>
        {hoveredCustom === "custom" && (
          <div
            ref={submenuRef}
            className={cn(
              "absolute top-0 z-50",
              submenuSide === "right" ? "left-full ml-2" : "right-full mr-2",
            )}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <NumericValueInput
              label="Value"
              value={customValue}
              onChange={setCustomValue}
              onCommit={() => {
                const num = Number(customValue);
                if (customValue !== "" && !isNaN(num)) onCommit(num);
              }}
              placeholder="Enter days"
            />
          </div>
        )}
      </div>
    </div>
  );
};

const AdStartedSidePanel = ({
  onCommit,
  selectedAccount,
  allowedDateRange,
}: {
  onCommit: (
    value: { start_date: string; end_date: string; label: TimeInterval },
    operatorKey: string,
  ) => void;
  selectedAccount: { time_zone?: string } | null;
  allowedDateRange: { first_allowed_date?: Date; last_allowed_date?: Date } | null;
}) => {
  const [customDialogOpen, setCustomDialogOpen] = useState(false);
  const timezone = selectedAccount?.time_zone ?? "UTC";

  const handlePresetClick = (daysAgo: number) => {
    const now = new Date();
    const targetDate = new Date(now);
    targetDate.setDate(targetDate.getDate() - daysAgo);
    const dateStr = targetDate.toLocaleDateString("en-CA", { timeZone: timezone });
    onCommit(
      { start_date: dateStr, end_date: dateStr, label: TimeInterval.CUSTOM },
      "DATE_ON_OR_AFTER",
    );
  };

  return (
    <div className={SQUIRCLE_PANEL_P1_180} onMouseDown={(e) => e.stopPropagation()}>
      {AD_STARTED_PRESETS.map((preset) => (
        <div
          key={preset.daysAgo}
          onClick={() => handlePresetClick(preset.daysAgo)}
          className={MENU_ITEM_CLS}
        >
          <span className={MENU_TEXT_CLS}>{preset.label}</span>
        </div>
      ))}
      <Dialog open={customDialogOpen} onOpenChange={setCustomDialogOpen}>
        <DialogTrigger asChild>
          <div className={MENU_ITEM_CLS}>
            <span className={MENU_TEXT_CLS}>Custom date or timeframe</span>
          </div>
        </DialogTrigger>
        <DialogContent className="p-0 border-none rounded-[12px] w-full" hideCloseButton>
          <DateRangePicker
            selected={null}
            dateAggregate=""
            onSelect={() => {}}
            first_allowed_date={allowedDateRange?.first_allowed_date}
            last_allowed_date={allowedDateRange?.last_allowed_date}
            onApply={(range) => {
              if (!range) return;
              const currentTime = new Date();
              const adjustedStart = adjustDateWithinAllowedRange(
                range.from,
                currentTime,
                allowedDateRange,
              );
              const adjustedEnd = adjustDateWithinAllowedRange(
                range.to,
                currentTime,
                allowedDateRange,
              );
              const startStr = adjustedStart.toLocaleDateString("en-CA", {
                timeZone: timezone,
              });
              const endStr = adjustedEnd.toLocaleDateString("en-CA", {
                timeZone: timezone,
              });
              const operatorKey =
                startStr !== endStr ? "DATE_BETWEEN" : "DATE_ON_OR_AFTER";
              onCommit(
                {
                  start_date: startStr,
                  end_date: endStr,
                  label: range.label ?? TimeInterval.CUSTOM,
                },
                operatorKey,
              );
              setCustomDialogOpen(false);
            }}
            timezone={timezone}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

const GROUPED_OP_PRIMARY_LABELS = ["more than", "less than"];

const GroupedOperatorDropdownContent = ({
  operators,
  selectedOperatorKey,
  onSelectOperator,
}: {
  operators: Array<{ key: string; label: string }>;
  selectedOperatorKey: string;
  onSelectOperator: (operatorKey: string) => void;
}) => {
  const primaryOps = GROUPED_OP_PRIMARY_LABELS.map((label) =>
    operators.find((op) => op.label.toLowerCase() === label),
  ).filter(Boolean) as Array<{ key: string; label: string }>;
  const moreOps = operators.filter(
    (op) => !GROUPED_OP_PRIMARY_LABELS.includes(op.label.toLowerCase()),
  );

  const selectedIsInMore = moreOps.some((op) => op.key === selectedOperatorKey);
  const [moreExpanded, setMoreExpanded] = useState(selectedIsInMore);

  const renderItem = (op: { key: string; label: string }) => (
    <SqDropdownMenuItem
      key={op.key}
      isSelected={op.key === selectedOperatorKey}
      onClick={() => onSelectOperator(op.key)}
      className="cursor-pointer"
    >
      <span className={MENU_TEXT_CLS}>{op.label}</span>
    </SqDropdownMenuItem>
  );

  return (
    <SqDropdownMenuGroup>
      {primaryOps.map(renderItem)}
      {moreOps.length > 0 && (
        <>
          <div
            onClick={() => setMoreExpanded((p) => !p)}
            className={cn(MENU_ITEM_CLS, "flex items-center justify-between gap-2")}
          >
            <span
              className={`text-xs font-medium leading-[20px] ${
                moreExpanded ? "text-[#02185966]" : "text-[#021859CC]"
              }`}
            >
              More options
            </span>
            <LuChevronDown
              className={`h-4 w-4 text-brand-primary-new/40 transition-transform duration-200 ${
                moreExpanded ? "" : "-rotate-90"
              }`}
            />
          </div>
          {moreExpanded && moreOps.map(renderItem)}
        </>
      )}
    </SqDropdownMenuGroup>
  );
};

const OperatorSidePanel = ({
  operators,
  onSelect,
  submenuSide = "right",
  prefix,
  suffix,
}: {
  operators: Array<{ key: string; label: string }>;
  onSelect: (operatorKey: string, value: number | { min: number; max: number }) => void;
  submenuSide?: "left" | "right";
  prefix?: string;
  suffix?: string;
}) => {
  const opContainerRef = useRef<HTMLDivElement>(null);
  const opSubmenuRef = useRef<HTMLDivElement>(null);
  const [moreExpanded, setMoreExpanded] = useState(false);
  const [hoveredOp, setHoveredOp] = useState<string | null>(null);
  const [singleValue, setSingleValue] = useState("");
  const [minValue, setMinValue] = useState("");
  const [maxValue, setMaxValue] = useState("");

  const { requestHover } = useSimpleSubMenuIntent({
    containerRef: opContainerRef,
    submenuRef: opSubmenuRef,
    activeKey: hoveredOp,
    setActiveKey: setHoveredOp,
    submenuSide,
  });

  useSubmenuBottomClamp(opSubmenuRef, hoveredOp);

  useEffect(() => {
    setSingleValue("");
    setMinValue("");
    setMaxValue("");
  }, [hoveredOp]);

  const PRIMARY_OP_LABELS = ["more than", "less than"];
  const primaryOps = PRIMARY_OP_LABELS.map((label) =>
    operators.find((op) => op.label.toLowerCase() === label),
  ).filter(Boolean) as Array<{ key: string; label: string }>;
  const moreOps = operators.filter(
    (op) => !PRIMARY_OP_LABELS.includes(op.label.toLowerCase()),
  );

  const isBetween = (key: string) => key === "BETWEEN";

  const renderValueSubmenu = (op: { key: string; label: string }) => {
    if (op.key !== hoveredOp) return null;
    const handleCommitSingle = () => {
      const num = singleValue === "" ? 0 : Number(singleValue);
      if (singleValue !== "" && !isNaN(num)) {
        onSelect(op.key, num);
        setHoveredOp(null);
        setSingleValue("");
      }
    };
    const handleCommitBetween = () => {
      const min = minValue === "" ? 0 : Number(minValue);
      const max = maxValue === "" ? 0 : Number(maxValue);
      if ((minValue !== "" || maxValue !== "") && !isNaN(min) && !isNaN(max)) {
        onSelect(op.key, { min, max });
        setHoveredOp(null);
        setMinValue("");
        setMaxValue("");
      }
    };
    if (isBetween(op.key)) {
      return (
        <div
          ref={opSubmenuRef}
          className={cn(
            "absolute top-0 z-50",
            submenuSide === "right" ? "left-full ml-2" : "right-full mr-2",
          )}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <BetweenValueInput
            minValue={minValue}
            maxValue={maxValue}
            onMinChange={setMinValue}
            onMaxChange={setMaxValue}
            onCommit={handleCommitBetween}
            prefix={prefix}
            suffix={suffix}
          />
        </div>
      );
    }
    return (
      <div
        ref={opSubmenuRef}
        className={cn(
          "absolute top-0 z-50",
          submenuSide === "right" ? "left-full ml-2" : "right-full mr-2",
        )}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <NumericValueInput
          value={singleValue}
          onChange={setSingleValue}
          onCommit={handleCommitSingle}
          placeholder="Enter value"
          prefix={prefix}
          suffix={suffix}
        />
      </div>
    );
  };

  const operatorItem = (op: { key: string; label: string }) => (
    <div key={op.key} className="relative" onMouseEnter={() => requestHover(op.key)}>
      <div className={cn(MENU_ITEM_CLS, hoveredOp === op.key && "bg-hover-gray")}>
        <span className={MENU_TEXT_CLS}>{op.label}</span>
      </div>
      {renderValueSubmenu(op)}
    </div>
  );

  return (
    <div
      ref={opContainerRef}
      className={SQUIRCLE_PANEL_P1_160}
      onMouseDown={(e) => e.stopPropagation()}
      onMouseLeave={() => requestHover(null)}
    >
      {primaryOps.map((op) => operatorItem(op))}
      {moreOps.length > 0 && (
        <>
          <div
            onClick={() => setMoreExpanded((p) => !p)}
            onMouseEnter={() => requestHover(null)}
            className={cn(MENU_ITEM_CLS, "flex items-center justify-between gap-2")}
          >
            <span
              className={`text-xs font-medium leading-[20px] ${
                moreExpanded ? "text-[#02185966]" : "text-[#021859CC]"
              }`}
            >
              More options
            </span>
            <LuChevronDown
              className={`h-4 w-4 text-brand-primary-new/40 transition-transform duration-200 ${
                moreExpanded ? "" : "-rotate-90"
              }`}
            />
          </div>
          {moreExpanded && moreOps.map((op) => operatorItem(op))}
        </>
      )}
    </div>
  );
};

const RECENT_TERMS_KEY_PREFIX = "filter-recent-";
const MAX_RECENT_TERMS_STORED = 10;
const MAX_RECENT_TERMS_SHOWN = 5;

function getRecentTerms(accountId: string, assetType: string): string[] {
  try {
    const raw = localStorage.getItem(`${RECENT_TERMS_KEY_PREFIX}${accountId}-${assetType}`);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.slice(0, MAX_RECENT_TERMS_STORED) : [];
  } catch {
    return [];
  }
}

function addRecentTerm(accountId: string, assetType: string, term: string): void {
  const existing = getRecentTerms(accountId, assetType);
  const updated = [term, ...existing.filter((t) => t !== term)].slice(0, MAX_RECENT_TERMS_STORED);
  localStorage.setItem(`${RECENT_TERMS_KEY_PREFIX}${accountId}-${assetType}`, JSON.stringify(updated));
}

function removeRecentTerm(accountId: string, assetType: string, term: string): void {
  const existing = getRecentTerms(accountId, assetType);
  const updated = existing.filter((t) => t !== term);
  localStorage.setItem(`${RECENT_TERMS_KEY_PREFIX}${accountId}-${assetType}`, JSON.stringify(updated));
}

const HoverOnlyTooltip = ({
  children,
  content,
  delayMs = 500,
}: {
  children: React.ReactNode;
  content: React.ReactNode;
  delayMs?: number;
}) => {
  const [open, setOpen] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const spanRef = useRef<HTMLSpanElement>(null);

  const dismiss = useCallback(() => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    setOpen(false);
  }, []);

  const hasOpenOverlay = useCallback(() => {
    const el = spanRef.current;
    if (!el) return false;
    return !!el.querySelector('[data-state="open"]');
  }, []);

  const handleEnter = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      if (!hasOpenOverlay()) setOpen(true);
    }, delayMs);
  }, [delayMs, hasOpenOverlay]);

  useEffect(() => {
    dismiss();
  }, [content, dismiss]);

  useEffect(() => {
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, []);

  return (
    <SqTooltipProvider delayDuration={0}>
      <SqTooltip open={open}>
        <SqTooltipTrigger asChild>
          <span
            ref={spanRef}
            className="inline-flex"
            onMouseEnter={handleEnter}
            onMouseLeave={dismiss}
            onPointerDown={dismiss}
            onFocusCapture={dismiss}
          >
            {children}
          </span>
        </SqTooltipTrigger>
        <SqTooltipContent
          portal={true}
          side="top"
          align="start"
          className="max-w-[300px] pointer-events-none z-50 whitespace-normal break-words"
        >
          {content}
        </SqTooltipContent>
      </SqTooltip>
    </SqTooltipProvider>
  );
};

const TruncatedText = ({ text, className }: { text: string; className?: string }) => {
  const ref = useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (el) setIsTruncated(el.scrollWidth > el.clientWidth);
  }, [text]);

  const inner = (
    <span ref={ref} className={cn("truncate cursor-pointer", className)}>
      {text}
    </span>
  );

  if (!isTruncated) return inner;

  return (
    <SqTooltipProvider delayDuration={500}>
      <SqTooltip>
        <SqTooltipTrigger asChild>{inner}</SqTooltipTrigger>
        <SqTooltipContent portal={true} side="top" align="start" className="max-w-[300px] z-50">
          {text}
        </SqTooltipContent>
      </SqTooltip>
    </SqTooltipProvider>
  );
};

const AssetSearchSidePanel = ({
  choice,
  onCommit,
  onRemove,
  onEdit,
  onClose,
  initialSelectedItems,
}: {
  choice: NonNullable<DataConfigurations["filter"]["filter_choices"]>[number];
  onCommit: (value: string, filterId?: string) => string | undefined | void;
  onRemove?: (value: string) => void;
  onEdit?: (oldValue: string, newValue: string) => void;
  onClose: () => void;
  initialSelectedItems?: string[];
}) => {
  const { accountId } = useParams();
  const assetType = choice.key.split("_")[0];
  const [searchTerm, setSearchTerm] = useState("");
  const [assets, setAssets] = useState<string[]>([]);
  const [isBackendLoading, setIsBackendLoading] = useState(true);
  const [selectedItems, setSelectedItems] = useState<string[]>(
    initialSelectedItems ?? [],
  );
  const [recentTerms, setRecentTerms] = useState<string[]>(() => getRecentTerms(accountId ?? "", assetType));
  const [flashItem, setFlashItem] = useState<string | null>(null);
  const [editingItem, setEditingItem] = useState<string | null>(null);
  const [editingValue, setEditingValue] = useState("");
  const [editingWidth, setEditingWidth] = useState<number | null>(null);
  const activeFilterIdRef = useRef<string | undefined>(undefined);
  const fetchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const flashTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const editInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setTimeout(() => inputRef.current?.focus(), 50);
  }, []);

  useEffect(() => {
    if (!accountId) return;

    const trimmed = searchTerm.trim();

    const fullList = assetCache.get(getCacheKey(accountId, assetType, ""));
    const cacheFiltered = fullList
      ? fullList.filter((item) => item.toLowerCase().includes(trimmed.toLowerCase()))
      : [];
    setAssets(cacheFiltered);

    const controller = new AbortController();
    setIsBackendLoading(true);

    const runFetch = () => {
      (async () => {
        try {
          const results = await fetchAssetsWithCache(accountId, assetType, trimmed, {
            signal: controller.signal,
          });
          setAssets((prev) => [...prev, ...results.filter((r) => !prev.includes(r))]);
        } catch {
          /* cancelled */
        } finally {
          setIsBackendLoading(false);
        }
      })();
    };

    if (trimmed === "") {
      runFetch();
    } else {
      if (fetchTimeoutRef.current) clearTimeout(fetchTimeoutRef.current);
      fetchTimeoutRef.current = setTimeout(runFetch, 300);
    }

    return () => {
      controller.abort();
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
        fetchTimeoutRef.current = null;
      }
    };
  }, [searchTerm, accountId, assetType]);

  useEffect(() => {
    return () => {
      if (flashTimeoutRef.current) clearTimeout(flashTimeoutRef.current);
    };
  }, []);

  const addItem = (value: string) => {
    const trimmed = value.trim();
    if (!trimmed || selectedItems.includes(trimmed)) return;
    setSelectedItems((prev) => [...prev, trimmed]);
    const returnedId = onCommit(trimmed, activeFilterIdRef.current);
    if (returnedId) activeFilterIdRef.current = returnedId;
    setSearchTerm("");
    addRecentTerm(accountId ?? "", assetType, trimmed);
    setRecentTerms(getRecentTerms(accountId ?? "", assetType));
    if (flashTimeoutRef.current) clearTimeout(flashTimeoutRef.current);
    setFlashItem(trimmed);
    flashTimeoutRef.current = setTimeout(() => setFlashItem(null), 400);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const removeItem = (value: string) => {
    setSelectedItems((prev) => prev.filter((v) => v !== value));
    onRemove?.(value);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const startEditing = (value: string, el: HTMLElement) => {
    setEditingWidth(el.offsetWidth);
    setEditingItem(value);
    setEditingValue(value);
    setTimeout(() => editInputRef.current?.select(), 0);
  };

  const commitEdit = () => {
    if (!editingItem) return;
    const trimmed = editingValue.trim();
    if (!trimmed || trimmed === editingItem) {
      setEditingItem(null);
      setEditingWidth(null);
      return;
    }
    if (selectedItems.includes(trimmed)) {
      removeItem(editingItem);
      setEditingItem(null);
      setEditingWidth(null);
      return;
    }
    setSelectedItems((prev) => prev.map((v) => (v === editingItem ? trimmed : v)));
    if (onEdit) {
      onEdit(editingItem, trimmed);
    } else {
      onRemove?.(editingItem);
      onCommit(trimmed, activeFilterIdRef.current);
    }
    addRecentTerm(accountId ?? "", assetType, trimmed);
    setRecentTerms(getRecentTerms(accountId ?? "", assetType));
    setEditingItem(null);
    setEditingWidth(null);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const cancelEdit = () => {
    setEditingItem(null);
    setEditingWidth(null);
  };

  const filteredAssets = assets.filter((a) => !selectedItems.includes(a));

  return (
    <div
      className="w-[403px] flex flex-col max-h-[460px] bg-white rounded-[12px] border border-[#E7E8E9] shadow-md overflow-hidden"
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div className="flex flex-wrap items-center gap-1 px-2 mx-1 mt-[4px] mb-[6px] min-h-7 py-1 bg-selected-gray rounded-[8px] shrink-0">
        <RiSearchLine size={14} color="#02185966" className="shrink-0" />
        {selectedItems.map((item) =>
          editingItem === item ? (
            <input
              key={item}
              ref={editInputRef}
              autoFocus
              className="inline-flex items-center h-[24px] box-border px-1.5 py-0 rounded-[4px] bg-[#FFFFFF] border border-[#CCD1DE] text-[#67749B] text-[12px] leading-none outline-none"
              style={{ width: editingWidth ? `${editingWidth}px` : undefined }}
              value={editingValue}
              onChange={(e) => setEditingValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") commitEdit();
                if (e.key === "Escape") cancelEdit();
                e.stopPropagation();
              }}
              onBlur={commitEdit}
            />
          ) : (
            <span
              key={item}
              className={cn(
                "inline-flex items-center gap-0.5 max-w-[180px] h-[24px] box-border pl-1.5 pr-0 py-0 rounded-[4px] bg-[#FFFFFF] border border-[#E7E8E9] text-[#67749B] text-[12px] leading-none whitespace-nowrap transition-colors cursor-pointer [&:hover:not(:has(button:hover))]:bg-[#F5F6F8]",
                flashItem === item && "animate-flash-tag",
              )}
              onClick={(e) => startEditing(item, e.currentTarget)}
            >
              <TruncatedText text={item} />
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  removeItem(item);
                }}
                className="shrink-0 p-0.5 rounded-sm hover:bg-[#E7E8E9] transition-colors cursor-pointer"
              >
                <RiCloseLine size={16} className="text-[#CCD1DE]" />
              </button>
            </span>
          ),
        )}
        <input
          ref={inputRef}
          autoFocus
          className="flex flex-1 min-w-[60px] bg-transparent py-1 pl-1 relative top-[1px] text-[12px] outline-none transition-all text-brand-primary-new/80 placeholder:text-brand-primary-new/40"
          type="text"
          placeholder={selectedItems.length > 0 ? "Add more…" : `Search ${assetType}`}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && searchTerm.trim()) {
              addItem(searchTerm);
            }
            if (e.key === "Backspace" && !searchTerm && selectedItems.length > 0) {
              removeItem(selectedItems[selectedItems.length - 1]);
            }
            if (e.key === "Escape") {
              onClose();
            }
          }}
        />
      </div>
      {searchTerm && (
        <div
          className="text-[12px] !text-[#02185999] cursor-pointer px-2 pb-2 flex gap-1"
          onClick={() => addItem(searchTerm)}
        >
          <TextSelect className="h-[16px] w-[16px] shrink-0" />
          <div>
            <span>Select all results that contain&nbsp;</span>
            <span className="text-[12px] text-brand-primary-new">{searchTerm}</span>
          </div>
        </div>
      )}
      {!searchTerm && recentTerms.filter((t) => !selectedItems.includes(t)).length > 0 && (
        <div className="flex flex-col gap-2 px-2 pt-0.5 pb-1.5 shrink-0">
          <div className="flex items-center gap-1 text-[#67749B]">
            <RiTimeLine size={14} className="text-[#CCD1DE]" />
            <span className="text-[12px] pt-[2px]">Recently used</span>
          </div>
          <div className="flex items-center gap-1 overflow-x-auto scrollbar-hide w-full">
              {recentTerms
                .filter((t) => !selectedItems.includes(t))
                .slice(0, MAX_RECENT_TERMS_SHOWN)
                .map((term) => (
                  <span
                    key={term}
                    className="inline-flex items-center gap-0.5 max-w-[180px] h-[24px] box-border pl-1.5 pr-0 py-0 rounded-[4px] bg-[#FFF9F7] border border-[#FFDACC] text-[#FF8F66] text-[12px] leading-none whitespace-nowrap cursor-pointer hover:bg-[#FFF4F0] shrink-0"
                    onClick={() => addItem(term)}
                  >
                    <TruncatedText text={term} />
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        removeRecentTerm(accountId ?? "", assetType, term);
                        setRecentTerms(getRecentTerms(accountId ?? "", assetType));
                      }}
                      className="shrink-0 p-0.5 rounded-sm hover:bg-[#FFDACC] transition-colors cursor-pointer"
                    >
                      <RiCloseLine size={16} className="text-[#FFB599]" />
                    </button>
                  </span>
                ))}
          </div>
        </div>
      )}
      <Separator />
      <div className="flex flex-col flex-grow overflow-y-auto">
        {filteredAssets.length > 0 ? (
          filteredAssets.map((asset_name) => (
            <div
              className="text-[12px] !text-brand-primary-800 p-2 cursor-pointer hover:bg-[#F6F6F6] break-all"
              key={asset_name}
              onClick={() => addItem(asset_name)}
            >
              {asset_name}
            </div>
          ))
        ) : isBackendLoading ? (
          <div className="my-auto p-2">
            <Shimmer initialRows={7} maxRows={7} />
          </div>
        ) : (
          <div className="h-full text-[12px] w-[100%] text-center my-auto text-[#021859B2] py-4">
            No results found
          </div>
        )}
      </div>
    </div>
  );
};

const FilterDropdownContent = ({
  tempFilter,
  setTempFilter,
  dataConfigFilter,
  onApply,
  onCancel,
  isKeySelectionMode,
  setIsKeySelectionMode,
  availableFilterOnOptions,
  hasMultipleFilterOnOptions,
  updateTempFilterWithFilterOn,
  allowedDateRange,
  clearSelection,
}: FilterDropdownContentProps) => {
  const { selectedAccount } = useGlobalStore();
  const [expandedChoices, setExpandedChoices] = useState<Record<string, boolean>>({});
  const [filterKeyDropdownOpen, setFilterKeyDropdownOpen] = useState(0);
  const [dateDialogOpenMap, setDateDialogOpenMap] = useState<Record<number, boolean>>({});

  const [searchTerm, setSearchTerm] = useState<string>("");

  // Auto-expand sections when search matches items within them
  useEffect(() => {
    if (!searchTerm.trim()) {
      // If search is empty, collapse all sections
      setExpandedChoices({});
      return;
    }
    const newExpandedChoices: Record<string, boolean> = {};
    const searchLower = searchTerm.toLowerCase();
    // Check each parent choice with children
    dataConfigFilter?.filter_choices
      .filter((choice) => "children" in choice)
      .forEach((choice) => {
        if (choice.children) {
          // Check if any child matches the search term
          const hasMatchingChild = choice.children.some((child) =>
            child.label.toLowerCase().includes(searchLower),
          );

          if (hasMatchingChild) {
            newExpandedChoices[choice.key] = true;
          }
        }
      });
    setExpandedChoices(newExpandedChoices);
  }, [searchTerm, dataConfigFilter]);

  const handleFilterClick = (
    item: NonNullable<DataConfigurations["filter"]["filter_choices"]>[number],
    child?: NonNullable<(typeof item)["children"]>[number],
  ) => {
    // Use the child if provided, otherwise use the item
    const selectedFilter = child || item;
    const firstOperator = dataConfigFilter?.filter_types.find(
      (type) => type.type === selectedFilter.filter_type,
    )?.operators[0];

    setTempFilter((prev) => {
      // If we're in key selection mode (first time selecting), create new filter
      if (isKeySelectionMode) {
        const newFilter = {
          id: crypto.randomUUID(),
          key: selectedFilter.key,
          field: "field" in selectedFilter ? selectedFilter.field : "",
          label: selectedFilter.label,
          filter_type: selectedFilter.filter_type,
          sub_type: child?.sub_type,
          type: selectedFilter?.type,
          conditions: [
            {
              operator: firstOperator?.key ?? "",
              value: getInitialValueForFilterType(
                selectedFilter.filter_type,
                selectedFilter?.type,
              ),
              label: selectedFilter.label,
              key: selectedFilter.key,
              parent_key: child ? item.key : selectedFilter.key,
            },
          ],
        };
        return updateTempFilterWithFilterOn(newFilter);
      }

      // If we're changing the filter key for an existing filter, preserve conditions
      if (prev && prev.conditions && prev.conditions.length > 0) {
        // Check if we're changing the filter key for a specific condition (OR condition)
        const currentFilterKeyDropdownOpen = filterKeyDropdownOpen;
        if (currentFilterKeyDropdownOpen > 1) {
          // We're changing the key for a specific OR condition
          const conditionIndex = currentFilterKeyDropdownOpen - 1;
          const updatedConditions = [...prev.conditions];

          // Find the filter choice for the new key to get the correct filter type and operators
          let filterChoice = dataConfigFilter?.filter_choices.find(
            (choice) => choice.key === selectedFilter.key,
          );

          // If not found in regular choices, look in children
          if (!filterChoice && child) {
            filterChoice = dataConfigFilter?.filter_choices
              .filter((choice) => "children" in choice)
              .find((choice) =>
                choice.children?.some((c) => c.key === selectedFilter.key),
              );
          }

          const newFilterType = filterChoice?.filter_type || selectedFilter.filter_type;
          const newOperators = dataConfigFilter?.filter_types.find(
            (type) => type.type === newFilterType,
          )?.operators;
          const newOperator = newOperators?.[0]?.key ?? "";

          // Update only the specific condition's key and operator, reset value for different filter types
          const currentCondition = updatedConditions[conditionIndex];
          let currentFilterType =
            currentCondition.key && currentCondition.key !== prev.key
              ? dataConfigFilter?.filter_choices.find(
                  (choice) => choice.key === currentCondition.key,
                )?.filter_type
              : prev.filter_type;

          // If not found in regular choices, look in children
          if (
            !currentFilterType &&
            currentCondition.key &&
            currentCondition.key !== prev.key
          ) {
            const parentChoice = dataConfigFilter?.filter_choices
              .filter((choice) => "children" in choice)
              .find((choice) =>
                choice.children?.some((c) => c.key === currentCondition.key),
              );
            currentFilterType = parentChoice?.children?.find(
              (c) => c.key === currentCondition.key,
            )?.filter_type;
          }

          // Reset value if filter types are different
          const shouldResetValue = currentFilterType !== newFilterType;

          updatedConditions[conditionIndex] = {
            ...updatedConditions[conditionIndex],
            key: selectedFilter.key,
            parent_key: child ? item.key : selectedFilter.key, // Set parent_key to the parent category if it's a child
            label: selectedFilter.label, // Set the label for proper display
            operator: newOperator,
            value: shouldResetValue ? "" : currentCondition.value,
          };

          const newTempFilter = {
            ...prev,
            conditions: updatedConditions,
          };
          return updateTempFilterWithFilterOn(newTempFilter);
        } else {
          // We're changing the main filter key (first condition)
          // Only update the first condition, preserve other conditions as they are
          const updatedConditions = [...prev.conditions];

          // Update only the first condition
          const currentFilterType = prev.filter_type;
          const shouldResetValue = currentFilterType !== selectedFilter.filter_type;

          updatedConditions[0] = {
            ...updatedConditions[0],
            key: selectedFilter.key,
            parent_key: child ? item.key : selectedFilter.key,
            operator: firstOperator?.key ?? updatedConditions[0].operator,
            value: shouldResetValue ? "" : updatedConditions[0].value || "",
            label: selectedFilter.label,
          };

          const newTempFilter = {
            ...prev,
            key: selectedFilter.key,
            field: "field" in selectedFilter ? selectedFilter.field : "",
            label: selectedFilter.label,
            filter_type: selectedFilter.filter_type,
            sub_type: child?.sub_type,
            conditions: updatedConditions,
          };
          return updateTempFilterWithFilterOn(newTempFilter);
        }
      }

      // Fallback to creating new filter
      const fallbackFilter = {
        id: crypto.randomUUID(),
        key: selectedFilter.key,
        field: "field" in selectedFilter ? selectedFilter.field : "",
        label: selectedFilter.label,
        filter_type: selectedFilter.filter_type,
        sub_type: child?.sub_type,
        conditions: [
          {
            operator: firstOperator?.key ?? "",
            value: getInitialValueForFilterType(
              selectedFilter.filter_type,
              selectedFilter?.type,
            ),
            label: selectedFilter.label,
            key: selectedFilter.key,
            parent_key: child ? item.key : selectedFilter.key,
          },
        ],
      };
      return updateTempFilterWithFilterOn(fallbackFilter);
    });
    setIsKeySelectionMode(false);

    // Close the dropdown when a filter is selected
    setFilterKeyDropdownOpen(0);
  };

  const renderValueInput = (
    filter: SectionFilter | undefined,
    conditionIndex: number,
  ) => {
    const activeFilter = getActiveFilter(filter);
    if (!activeFilter) return null;
    const condition = activeFilter.conditions?.[conditionIndex];
    if (!condition) return null;

    // Get the filter type for this specific condition
    let conditionFilterType =
      condition.key && condition.key !== activeFilter.key
        ? dataConfigFilter?.filter_choices.find((choice) => choice.key === condition.key)
            ?.filter_type ||
          dataConfigFilter?.filter_choices
            .filter((choice) => "children" in choice)
            .find((choice) => choice.children?.some((c) => c.key === condition.key))
            ?.children?.find((c) => c.key === condition.key)?.filter_type ||
          activeFilter.filter_type
        : activeFilter.filter_type;

    // If still not found, use the condition's parent_key to find the filter type
    if (
      !conditionFilterType &&
      condition.parent_key &&
      condition.key !== activeFilter.key
    ) {
      const parentChoice = dataConfigFilter?.filter_choices
        .filter((choice) => "children" in choice)
        .find((choice) => choice.key === condition.parent_key);
      conditionFilterType =
        parentChoice?.children?.find((c) => c.key === condition.key)?.filter_type ||
        activeFilter.filter_type;
    }

    if (conditionFilterType === "date") {
      return (
        <Dialog
          open={dateDialogOpenMap[conditionIndex]}
          onOpenChange={(open) =>
            setDateDialogOpenMap((prev) => ({ ...prev, [conditionIndex]: open }))
          }
        >
          <DialogTrigger asChild>
            <SqButton
              variant="outline"
              className="w-[185px] justify-between items-center h-8 px-3 py-2"
              shadow={"sm"}
            >
              {condition.value ? (
                <span className="overflow-hidden text-brand-primary-new text-ellipsis text-xs font-normal leading-[19.157px] tracking-[0.24px]">
                  {(() => {
                    const { start_date, end_date } = condition.value as {
                      start_date: string;
                      end_date: string;
                    };
                    if (start_date && end_date) {
                      return formatDateRange({
                        start_date: start_date,
                        end_date: end_date,
                        timezone: selectedAccount?.time_zone,
                      });
                    }
                    return "Select date range";
                  })()}
                </span>
              ) : (
                <span className="overflow-hidden text-[#A4ACC3] text-ellipsis text-xs font-normal leading-[19.157px] tracking-[0.24px]">
                  {condition.operator === "DATE_ON_OR_AFTER" ||
                  condition.operator === "DATE_ON_OR_BEFORE"
                    ? "Select date"
                    : "Select date range"}
                </span>
              )}
              <RiArrowDownSLine size={14} className="ml-2 shrink-0" color="#9aa3bd" />
            </SqButton>
          </DialogTrigger>
          <DialogContent
            className="p-0 border-none rounded-[12px] w-full"
            hideCloseButton
          >
            <DateRangePicker
              singleSelect={
                condition.operator === "DATE_ON_OR_AFTER" ||
                condition.operator === "DATE_ON_OR_BEFORE"
              }
              first_allowed_date={allowedDateRange?.first_allowed_date}
              last_allowed_date={allowedDateRange?.last_allowed_date}
              selected={
                condition.value
                  ? {
                      from: fromZonedTime(
                        (condition.value as { start_date: string }).start_date,
                        selectedAccount?.time_zone ?? "UTC",
                      ),
                      to: fromZonedTime(
                        (condition.value as { end_date: string }).end_date,
                        selectedAccount?.time_zone ?? "UTC",
                      ),
                      label:
                        (condition.value as { label: TimeInterval }).label ??
                        TimeInterval.CUSTOM,
                    }
                  : null
              }
              dateAggregate=""
              onSelect={(range) => {
                if (!range) return;
                const currentTime = new Date();
                const adjustedStartDate = adjustDateWithinAllowedRange(
                  range.from,
                  currentTime,
                  allowedDateRange,
                );
                const adjustedEndDate = adjustDateWithinAllowedRange(
                  range.to,
                  currentTime,
                  allowedDateRange,
                );

                setTempFilter((prev) => {
                  if (!prev) return undefined;
                  const newConditions = [...(prev.conditions || [])];
                  newConditions[conditionIndex] = {
                    ...newConditions[conditionIndex],
                    value: {
                      start_date: adjustedStartDate.toLocaleDateString("en-CA", {
                        timeZone: selectedAccount?.time_zone ?? "UTC",
                      }),
                      end_date: adjustedEndDate.toLocaleDateString("en-CA", {
                        timeZone: selectedAccount?.time_zone ?? "UTC",
                      }),
                      label: range.label ?? TimeInterval.CUSTOM,
                    },
                  };
                  return { ...prev, conditions: newConditions };
                });
              }}
              onApply={(range) => {
                if (!range) return;
                const currentTime = new Date();
                const adjustedStartDate = adjustDateWithinAllowedRange(
                  range.from,
                  currentTime,
                  allowedDateRange,
                );
                const adjustedEndDate = adjustDateWithinAllowedRange(
                  range.to,
                  currentTime,
                  allowedDateRange,
                );

                setTempFilter((prev) => {
                  if (!prev) return undefined;
                  const newConditions = [...(prev.conditions || [])];
                  newConditions[conditionIndex] = {
                    ...newConditions[conditionIndex],
                    value: {
                      start_date: adjustedStartDate.toLocaleDateString("en-CA", {
                        timeZone: selectedAccount?.time_zone ?? "UTC",
                      }),
                      end_date: adjustedEndDate.toLocaleDateString("en-CA", {
                        timeZone: selectedAccount?.time_zone ?? "UTC",
                      }),
                      label: range.label ?? TimeInterval.CUSTOM,
                    },
                  };
                  return { ...prev, conditions: newConditions };
                });
                setDateDialogOpenMap((prev) => ({ ...prev, [conditionIndex]: false }));
              }}
              onCancel={() =>
                setDateDialogOpenMap((prev) => ({ ...prev, [conditionIndex]: false }))
              }
              autoAllowed={false}
              showAggregate={false}
              timezone={selectedAccount?.time_zone ?? "UTC"}
            />
          </DialogContent>
        </Dialog>
      );
    }

    if (conditionFilterType === "option" || conditionFilterType === "boolean") {
      // Find the filter choice for this condition from dataConfigurations
      const filterChoice = dataConfigFilter?.filter_choices.find((choice) => {
        return choice.key === condition.key;
      });

      let options = filterChoice?.options;
      let conditionSelectType = filterChoice?.type;

      // If not found in regular choices, look in children (for AI labels, metrics, etc.)
      if (!options) {
        const parentChoice = dataConfigFilter?.filter_choices
          .filter((choice) => "children" in choice)
          .find((choice) =>
            choice.children?.some((child) => child.key === condition.key),
          );

        if (parentChoice) {
          // Get the specific child that matches our condition key
          const childChoice = parentChoice.children?.find(
            (child) => child.key === condition.key,
          );
          options = childChoice?.options;
          conditionSelectType = childChoice?.type;
        }
      }

      // If options are not available in dataConfigFilter, use UniqueLabelsSelect as fallback
      if (!options || options.length === 0) {
        return (
          <UniqueLabelsSelect
            condition={condition}
            setTempFilter={setTempFilter}
            conditionIndex={conditionIndex}
            filter={activeFilter}
          />
        );
      }

      // Use the condition's select type from the filter choice, fallback to filter's type
      if (conditionSelectType === "single_select" || filter?.type === "single_select") {
        return (
          <SelectDropdown
            options={options.map((option) => ({
              key: option.key,
              label: option.label,
              value: option.key,
            }))}
            showTooltip={true}
            selected={condition.value as string}
            onChange={(newValue: string) => {
              setTempFilter((prev) => {
                if (!prev) return undefined;
                const newConditions = [...(prev.conditions || [])];
                newConditions[conditionIndex] = {
                  ...newConditions[conditionIndex],
                  value: newValue,
                };
                return { ...prev, conditions: newConditions };
              });
            }}
            placeholder={`Select ${condition?.label}`}
            className="w-[185px] h-8"
            contentClassName="w-[185px]"
            enableSearch={options.length > 5}
            searchPlaceholder="Search"
            sideOffset={7}
          />
        );
      }

      return (
        <SelectDropdown
          multiSelectSaveLabel="Apply"
          multiSelectShowSaveButton
          multiSelectCommitOnChange
          options={options.map((option) => ({
            key: option.key,
            label: option.label,
            value: option.key,
            ...(option.platform && {
              icon: () => {
                if (!option?.platform) return null;
                return (
                  <img
                    className="h-[14px] w-[14px] flex-shrink-0"
                    src={getPlatformIcon(option.platform)}
                  />
                );
              },
            }),
          }))}
          showTooltip={true}
          selected=""
          onChange={() => {}}
          selectedValues={Array.isArray(condition.value) ? condition.value : []}
          onChangeMultiple={(newValues: string[]) => {
            setTempFilter((prev) => {
              if (!prev) return undefined;
              const newConditions = [...(prev.conditions || [])];
              newConditions[conditionIndex] = {
                ...newConditions[conditionIndex],
                value: newValues,
              };
              return { ...prev, conditions: newConditions };
            });
          }}
          multiSelect={true}
          placeholder={`Select ${condition?.label}`}
          className="w-[185px] h-8"
          enableSearch={true}
          searchPlaceholder="Search"
          sideOffset={5}
          alignOffset={0}
        />
      );
    }

    if (
      ["ad_name", "adset_name", "campaign_name"].includes(
        condition.key || activeFilter?.key,
      )
    ) {
      return (
        <RenderValueInputForAssets
          assetType={(condition.key || activeFilter?.key)?.split("_")[0]}
          setTempFilter={setTempFilter}
          conditionIndex={conditionIndex}
          value={
            typeof condition.value === "object"
              ? JSON.stringify(condition.value)
              : typeof condition.value === "string"
                ? condition.value
                : JSON.stringify(condition.value)
          }
        />
      );
    }
    if (condition.operator === "BETWEEN") {
      const minMaxValue = condition.value as { min: number; max: number } | undefined;
      return (
        <div className="flex gap-[6px] items-center">
          <SqInput
            shadow="sm"
            value={minMaxValue?.min !== undefined ? minMaxValue.min : ""}
            onChange={(e) => {
              setTempFilter((prev) => {
                if (!prev) return undefined;
                const newConditions = [...(prev.conditions || [])];
                const inputValue = e.target.value;
                const numValue = inputValue === "" ? undefined : Number(inputValue);
                const currentValue = newConditions[conditionIndex].value as {
                  min?: number;
                  max?: number;
                };
                newConditions[conditionIndex] = {
                  ...newConditions[conditionIndex],
                  // Don't reset the key or parent_key - preserve the condition's existing properties
                  value: {
                    ...currentValue,
                    ...(numValue !== undefined && { min: numValue }),
                    ...(numValue === undefined &&
                      currentValue.min !== undefined && { min: undefined }),
                  } as { min: number; max: number },
                };
                return { ...prev, conditions: newConditions };
              });
            }}
            className="flex w-[90px] items-center gap-[6px] rounded-[8px] overflow-hidden text-brand-primary-new/80 text-ellipsis text-[12px] font-normal leading-[19.157px] tracking-[0.24px] placeholder:text-[#02185966]"
            type="number"
            placeholder="Min"
          />
          <SqInput
            shadow="sm"
            value={minMaxValue?.max !== undefined ? minMaxValue.max : ""}
            onChange={(e) => {
              setTempFilter((prev) => {
                if (!prev) return undefined;
                const newConditions = [...(prev.conditions || [])];
                const inputValue = e.target.value;
                const numValue = inputValue === "" ? undefined : Number(inputValue);
                const currentValue = newConditions[conditionIndex].value as {
                  min?: number;
                  max?: number;
                };
                newConditions[conditionIndex] = {
                  ...newConditions[conditionIndex],
                  // Don't reset the key or parent_key - preserve the condition's existing properties
                  value: {
                    ...currentValue,
                    ...(numValue !== undefined && { max: numValue }),
                    ...(numValue === undefined &&
                      currentValue.max !== undefined && { max: undefined }),
                  } as { min: number; max: number },
                };
                return { ...prev, conditions: newConditions };
              });
            }}
            className="flex w-[90px] items-center gap-[6px] rounded-[8px] overflow-hidden text-brand-primary-new/80 text-ellipsis text-[12px] font-normal leading-[19.157px] tracking-[0.24px] placeholder:text-[#02185966]"
            type="number"
            placeholder="Max"
          />
        </div>
      );
    }
    return (
      <SqInput
        shadow="sm"
        value={
          typeof condition.value === "object"
            ? JSON.stringify(condition.value)
            : conditionFilterType === "number"
              ? condition.value === "" || condition.value === undefined
                ? ""
                : condition.value
              : condition.value !== undefined
                ? condition.value
                : ""
        }
        onChange={(e) => {
          setTempFilter((prev) => {
            if (!prev) return undefined;
            const newConditions = [...(prev.conditions || [])];
            const inputValue = e.target.value;
            const value =
              conditionFilterType === "number"
                ? inputValue === ""
                  ? ""
                  : Number(inputValue)
                : inputValue;
            newConditions[conditionIndex] = {
              ...newConditions[conditionIndex],
              // Don't reset the key or parent_key - preserve the condition's existing properties
              value,
            };
            return { ...prev, conditions: newConditions };
          });
        }}
        className="flex w-[185px] items-center gap-[6px] rounded-[8px] overflow-hidden text-brand-primary-new/80 text-ellipsis text-[12px] font-normal leading-[19.157px] tracking-[0.24px] placeholder:text-[#02185966]"
        type={conditionFilterType === "number" ? "number" : "text"}
        placeholder="Value"
      />
    );
  };

  const deleteCondition = (index: number) => {
    setTempFilter((prev) => {
      if (!prev) return undefined;
      const conditions = [...(prev.conditions || [])];
      conditions.splice(index, 1);
      const newTempFilter = { ...prev, conditions };
      return updateTempFilterWithFilterOn(newTempFilter);
    });
  };

  return (
    <div
      className={`flex flex-col gap-[14px] ${
        isKeySelectionMode
          ? "bg-transparent rounded-[12px]"
          : "rounded-[8px] px-[18px] py-[12px] bg-white border-[1px] border-border-gray "
      }`}
    >
      {isKeySelectionMode ? (
        <FilterDropdown
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          dataConfigFilter={dataConfigFilter}
          expandedChoices={expandedChoices}
          setExpandedChoices={setExpandedChoices}
          handleFilterClick={handleFilterClick}
          clearSelection={clearSelection}
        />
      ) : (
        <>
          {availableFilterOnOptions.length > 0 && (
            <SqSelect
              value={tempFilter?.filter_on}
              onValueChange={(value) =>
                setTempFilter((prev) =>
                  prev ? { ...prev, filter_on: value as FilterOn } : prev,
                )
              }
              disabled={!hasMultipleFilterOnOptions}
            >
              <SqSelectTrigger
                className="w-fit flex items-center p-0 disabled:!opacity-100"
                showOutline={false}
                shadow="none"
                hoverBgColor={hasMultipleFilterOnOptions ? "white" : "transparent"}
                chevronClassName={`disabled:!text-[#9aa3bd66] ${!hasMultipleFilterOnOptions ? "!text-[#9aa3bd66]" : ""}`}
              >
                <IconFilter size={14} color="#02185966" className="shrink-0" />
                <span className="font-medium text-[12px] leading-[19.16px] tracking-[0.24px] text-[#021859] mx-1.5 translate-y-[1px]">
                  {FilterOnItems.find((f) => f.value === tempFilter?.filter_on)?.label}
                </span>
              </SqSelectTrigger>
              <SqSelectContent radius={12}>
                {FilterOnItems.filter((item) =>
                  availableFilterOnOptions.includes(item.value),
                ).map((item) => (
                  <SqSelectItem
                    radius={8}
                    key={item.key}
                    value={item.value}
                    className="text-[#021859CC] font-normal text-[12px] leading-[20px] tracking-[0]"
                  >
                    {item.label}
                  </SqSelectItem>
                ))}
              </SqSelectContent>
            </SqSelect>
          )}
          <AnimatePresence mode="popLayout" initial={false}>
            {tempFilter?.conditions?.map((condition, index) => (
              <motion.div
                key={index}
                {...FILTER_ROW_MOTION}
                className="flex gap-[12px] items-center self-end"
              >
                <div className="text-[12px] text-brand-primary-new/80">
                  {index === 0 ? "Where" : "OR"}
                </div>
                <div>
                  {/* Filters dropdowns */}
                  <SqDropdownMenu
                    open={filterKeyDropdownOpen === index + 1}
                    onOpenChange={(open) =>
                      setFilterKeyDropdownOpen(open ? index + 1 : 0)
                    }
                  >
                    <SqDropdownMenuTrigger className="focus:outline-none focus:ring-0">
                      <div
                        className={`h-8 min-w-[200px] flex justify-between items-center gap-[6px]`}
                      >
                        <span className="overflow-hidden text-brand-primary-new/80 text-ellipsis text-[12px] font-normal leading-[19.157px] tracking-[0.24px] truncate">
                          {condition.key && condition.key !== tempFilter?.key
                            ? dataConfigFilter?.filter_choices.find(
                                (choice) => choice.key === condition.key,
                              )?.label ||
                              dataConfigFilter?.filter_choices
                                .filter((choice) => "children" in choice)
                                .find((choice) =>
                                  choice.children?.some(
                                    (child) => child.key === condition.key,
                                  ),
                                )
                                ?.children?.find((child) => child.key === condition.key)
                                ?.label ||
                              condition.label ||
                              tempFilter?.label
                            : condition.label || tempFilter?.label}
                        </span>
                        <RiArrowDownSLine
                          size={14}
                          color="#9aa3bd"
                          className="shrink-0"
                        />
                      </div>
                    </SqDropdownMenuTrigger>
                    <SqDropdownMenuContent
                      hasOutline={false}
                      align="start"
                      className="w-auto p-0 rounded-[12px] border-0"
                    >
                      <FilterDropdown
                        searchTerm={searchTerm}
                        setSearchTerm={setSearchTerm}
                        dataConfigFilter={dataConfigFilter}
                        expandedChoices={expandedChoices}
                        setExpandedChoices={setExpandedChoices}
                        handleFilterClick={handleFilterClick}
                      />
                    </SqDropdownMenuContent>
                  </SqDropdownMenu>
                </div>
                {/* Show operator dropdown for all filter types */}
                <div>
                  <SqDropdownMenu>
                    <SqDropdownMenuTrigger className="focus:outline-none focus:ring-0">
                      <div className="h-8 w-[150px] flex justify-between items-center gap-[6px] rounded-[8px]">
                        <span className="overflow-hidden text-brand-primary-new/80 text-ellipsis text-[12px] font-normal leading-[19.157px] tracking-[0.24px] truncate">
                          {(() => {
                            // Get the filter type for this specific condition
                            const conditionFilterType =
                              condition.key && condition.key !== tempFilter?.key
                                ? dataConfigFilter?.filter_choices.find(
                                    (choice) => choice.key === condition.key,
                                  )?.filter_type ||
                                  dataConfigFilter?.filter_choices
                                    .filter((choice) => "children" in choice)
                                    .find((choice) =>
                                      choice.children?.some(
                                        (c) => c.key === condition.key,
                                      ),
                                    )
                                    ?.children?.find((c) => c.key === condition.key)
                                    ?.filter_type
                                : tempFilter?.filter_type;

                            return (
                              dataConfigFilter?.filter_types
                                .find((type) => type.type === conditionFilterType)
                                ?.operators.find((op) => op.key === condition.operator)
                                ?.label || condition.operator
                            );
                          })()}
                        </span>
                        <RiArrowDownSLine
                          size={14}
                          className="shrink-0"
                          color="#9aa3bd"
                        />
                      </div>
                    </SqDropdownMenuTrigger>
                    <SqDropdownMenuContent
                      align="start"
                      className="rounded-[8px]"
                      radius={12}
                    >
                      {(() => {
                        let conditionFilterType =
                          condition.key && condition.key !== tempFilter?.key
                            ? dataConfigFilter?.filter_choices.find(
                                (choice) => choice.key === condition.key,
                              )?.filter_type ||
                              dataConfigFilter?.filter_choices
                                .filter((choice) => "children" in choice)
                                .find((choice) =>
                                  choice.children?.some((c) => c.key === condition.key),
                                )
                                ?.children?.find((c) => c.key === condition.key)
                                ?.filter_type ||
                              tempFilter?.filter_type
                            : tempFilter?.filter_type;

                        if (
                          !conditionFilterType &&
                          condition.parent_key &&
                          condition.key !== tempFilter?.key
                        ) {
                          const parentChoice = dataConfigFilter?.filter_choices
                            .filter((choice) => "children" in choice)
                            .find((choice) => choice.key === condition.parent_key);
                          conditionFilterType =
                            parentChoice?.children?.find((c) => c.key === condition.key)
                              ?.filter_type || tempFilter?.filter_type;
                        }

                        const operators = dataConfigFilter?.filter_types.find(
                          (type) => type.type === conditionFilterType,
                        )?.operators;

                        if (!operators) return null;

                        const handleSelectOperator = (operatorKey: string) => {
                          setTempFilter((prev) => {
                            if (!prev) return undefined;
                            const newConditions = [...(prev.conditions || [])];
                            const currentCondition = newConditions[index];

                            const isSwitchingToSingleSelect =
                              (operatorKey === "DATE_ON_OR_AFTER" ||
                                operatorKey === "DATE_ON_OR_BEFORE") &&
                              conditionFilterType === "date" &&
                              currentCondition.value &&
                              typeof currentCondition.value === "object" &&
                              "start_date" in currentCondition.value &&
                              "end_date" in currentCondition.value;

                            let updatedValue = currentCondition.value;
                            if (isSwitchingToSingleSelect) {
                              const dateValue = currentCondition.value as {
                                start_date: string;
                                end_date: string;
                                label?: TimeInterval;
                              };
                              updatedValue = {
                                start_date: dateValue.start_date,
                                end_date: dateValue.start_date,
                                label: dateValue.label ?? TimeInterval.CUSTOM,
                              };
                            }

                            newConditions[index] = {
                              ...newConditions[index],
                              operator: operatorKey,
                              value: updatedValue,
                            };
                            return { ...prev, conditions: newConditions };
                          });
                        };

                        if (METRIC_SIDE_PANEL_TYPES.includes(conditionFilterType || "")) {
                          return (
                            <GroupedOperatorDropdownContent
                              operators={operators}
                              selectedOperatorKey={condition.operator || ""}
                              onSelectOperator={handleSelectOperator}
                            />
                          );
                        }

                        return (
                          <SqDropdownMenuGroup>
                            {operators.map((operator) => (
                              <SqDropdownMenuItem
                                isSelected={operator?.key === condition.operator}
                                key={operator.key}
                                onClick={() => handleSelectOperator(operator.key)}
                                className="cursor-pointer"
                              >
                                <span className={MENU_TEXT_CLS}>{operator.label}</span>
                              </SqDropdownMenuItem>
                            ))}
                          </SqDropdownMenuGroup>
                        );
                      })()}
                    </SqDropdownMenuContent>
                  </SqDropdownMenu>
                </div>
                <div key={`condition-${condition.key}-${index}`}>
                  {renderValueInput(tempFilter, index)}
                </div>
                <Button
                  className="text-[#67749B] border-0 hover:bg-white p-1 shadow-none"
                  variant={"outline"}
                  onClick={() => deleteCondition(index)}
                  disabled={(tempFilter?.conditions?.length || 0) <= 1}
                >
                  <Trash2 size={18} />
                </Button>
              </motion.div>
            ))}
          </AnimatePresence>

          <div className="flex justify-between items-center gap-2">
            <SqButton
              variant="outline"
              shadow={"sm"}
              className="text-[12px] gap-2 text-400 px-[14px] py-[6px] hover:bg-white h-8 items-center"
              onClick={() => {
                const operators = dataConfigFilter?.filter_types.find(
                  (type) => type.type === tempFilter?.filter_type,
                )?.operators;
                setTempFilter((prev) => {
                  if (!prev) return undefined;
                  const newConditions = [
                    ...(prev.conditions || []),
                    {
                      operator: operators?.[0]?.key ?? "",
                      value: getInitialValueForFilterType(
                        prev.filter_type || "",
                        prev.type,
                      ),
                      key: prev.key, // Set the same key as the main filter
                      parent_key: prev.conditions?.[0]?.parent_key || prev.key, // Preserve the parent_key from the first condition
                      label: prev.label, // Set the label for proper display
                    },
                  ];
                  const newTempFilter = { ...prev, conditions: newConditions };
                  return updateTempFilterWithFilterOn(newTempFilter);
                });
              }}
            >
              <RiAddLargeLine size={14} className="shrink-0" color="#9aa3bd" />
              <span className="font-[400] text-[12px] text-brand-primary-new/80 translate-y-[1px] leading-none">
                OR Condition
              </span>
            </SqButton>
            <div className="flex gap-2">
              <SqButton variant="outline" onClick={onCancel} size={"md"}>
                Cancel
              </SqButton>
              <TooltipProvider delayDuration={100}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <SqButton
                      variant="default"
                      size={"md"}
                      disabled={(() => {
                        // Check all conditions directly
                        const conditionsValid =
                          tempFilter?.conditions?.every(validateCondition) ?? true;

                        return !conditionsValid;
                      })()}
                      className="text-white border-0 cursor-pointer"
                      onClick={onApply}
                    >
                      Apply
                    </SqButton>
                  </TooltipTrigger>
                  <TooltipContent
                    align="start"
                    side="bottom"
                    className="squircle squircle-fill-[black]"
                  >
                    Applies filter only to this section
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

interface RenderValueInputForAssetsProps {
  value: string;
  assetType: string;
  setTempFilter: React.Dispatch<SetStateAction<SectionFilter | undefined>>;
  conditionIndex: number;
}
const RenderValueInputForAssets = (props: RenderValueInputForAssetsProps) => {
  const { value, setTempFilter, assetType, conditionIndex } = props;
  const { accountId } = useParams();
  const [assets, setAssets] = useState<string[]>([]);
  const [isBackendLoading, setIsBackendLoading] = useState<boolean>(true);
  const [searchTerm, setSearchTerm] = useState<string>(value);
  const [openAssetsPopover, setOpenAssetsPopover] = useState<boolean>(false);

  // Sync searchTerm with value prop when it changes
  useEffect(() => {
    setSearchTerm(value);
  }, [value]);

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!accountId) return;

    const trimmed = searchTerm.trim();

    const fullList = assetCache.get(getCacheKey(accountId, assetType, ""));
    const cacheFiltered = fullList
      ? fullList.filter((item) => item.toLowerCase().includes(trimmed.toLowerCase()))
      : [];
    setAssets(cacheFiltered);

    const controller = new AbortController();
    setIsBackendLoading(true);

    const runFetch = () => {
      (async () => {
        try {
          const results = await fetchAssetsWithCache(accountId, assetType, trimmed, {
            signal: controller.signal,
          });
          setAssets((prev) => [...prev, ...results.filter((r) => !prev.includes(r))]);
        } catch {
          /* cancelled */
        } finally {
          setIsBackendLoading(false);
        }
      })();
    };

    if (trimmed === "") {
      runFetch();
    } else {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(runFetch, 300);
    }

    return () => {
      controller.abort();
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [searchTerm, accountId, assetType]);

  return (
    <Popover open={openAssetsPopover} onOpenChange={setOpenAssetsPopover}>
      <PopoverTrigger asChild>
        <SqInput
          className="h-8 w-[185px] !bg-[#FFF] text-[12px] text-brand-primary-new/80 rounded-[8px] shadow-sm placeholder:text-[#02185966]"
          type="text"
          placeholder={`Enter ${assetType} name`}
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setTempFilter((prev) => {
              if (!prev) return undefined;
              const newConditions = [...(prev.conditions || [])];
              const value = e.target.value;
              newConditions[conditionIndex] = {
                ...newConditions[conditionIndex],
                value,
              };
              return { ...prev, conditions: newConditions };
            });
          }}
          onClick={(e) => {
            e.stopPropagation();
            setOpenAssetsPopover(true);
          }}
        />
      </PopoverTrigger>

      <PopoverContent
        className="flex flex-col w-72 p-0 mr-2 min-h-[240px] max-h-[360px] overflow-y-auto overflow-x-hidden break-all bg-[#FFF] radius-[3px] shadow-none"
        onOpenAutoFocus={(e) => {
          // Prevent the default focus behavior
          e.preventDefault();
        }}
        align="start"
      >
        <div
          className={`text-[12px] ${searchTerm ? "!text-[#02185999] cursor-pointer" : "!text-[rgba(2,24,89,0.40)]"} p-2 flex gap-1`}
          onClick={() => searchTerm && setOpenAssetsPopover(false)}
        >
          <TextSelect className="h-[16px] w-[16px] shrink-0" />

          <div>
            <span>Select all results that contain&nbsp;</span>
            <span className="text-[12px] text-brand-primary-new">{searchTerm}</span>
          </div>
        </div>
        <Separator />
        <div className="flex flex-col flex-grow">
          {assets.length > 0 ? (
            <>
              {assets.map((asset_name) => (
                <div
                  className="text-[12px] !text-brand-primary-800 p-2 cursor-pointer hover:bg-[#F6F6F6]"
                  key={asset_name}
                  onClick={() => {
                    setSearchTerm(asset_name);
                    setTempFilter((prev) => {
                      if (!prev) return undefined;
                      const newConditions = [...(prev.conditions || [])];
                      const value = asset_name;
                      newConditions[conditionIndex] = {
                        ...newConditions[conditionIndex],
                        value,
                      };
                      return { ...prev, conditions: newConditions };
                    });
                    setOpenAssetsPopover(false);
                  }}
                >
                  {asset_name}
                </div>
              ))}
            </>
          ) : isBackendLoading ? (
            <div className="my-auto p-2">
              <Shimmer initialRows={7} maxRows={7} />
            </div>
          ) : (
            <div className="h-full text-[12px] w-[100%] text-center my-auto text-[#021859B2]">
              No results found
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

// Add this new component for fetching and displaying unique labels
type UniqueLabelsSelectProps = {
  accountId?: string;
  reportId?: string;
  customTrigger?: React.ReactNode;
  condition: {
    operator: string;
    value:
      | string
      | string[]
      | number
      | { start_date: string; end_date: string; label: TimeInterval }
      | { min: number; max: number };
    key?: string;
  };
  setTempFilter: React.Dispatch<SetStateAction<SectionFilter | undefined>>;
  conditionIndex: number;
  filter: ReturnType<typeof getActiveFilter>;
};

const UniqueLabelsSelect = ({
  condition,
  setTempFilter,
  conditionIndex,
  customTrigger,
}: UniqueLabelsSelectProps) => {
  const { accountId } = useParams();
  const [uniqueLabels, setUniqueLabels] = useState<string[]>([]);
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pollingRef = useRef<NodeJS.Timeout | null>(null);
  const cancelTokenSourceRef = useRef<CancelTokenSource | null>(null);
  const filterId = condition?.key;

  const fetchLabels = useCallback(async () => {
    if (!accountId || !filterId) {
      setError("Missing account or filter ID");
      return;
    }

    try {
      // Cancel any existing request
      if (cancelTokenSourceRef.current) {
        cancelTokenSourceRef.current.cancel("New request initiated");
      }
      // Create new cancel token source
      cancelTokenSourceRef.current = axios.CancelToken.source();

      setIsPending(true);
      setError(null); // Clear any previous errors
      const response = await (
        getUniqueLabels as (
          accountId: string,
          labelId: string,
          sectionId?: string,
          cancelToken?: CancelTokenSource["token"],
        ) => Promise<{ status: string; unique_labels: string[] }>
      )(accountId, filterId, "", cancelTokenSourceRef.current.token);

      if (response.status === "success") {
        setUniqueLabels(response.unique_labels);
        setIsPending(false);
        // Clear any existing polling
        if (pollingRef.current) {
          clearTimeout(pollingRef.current);
          pollingRef.current = null;
        }
      } else if (response.status === "pending") {
        // Set the labels we have so far
        if (response.unique_labels && response.unique_labels.length > 0) {
          setUniqueLabels(response.unique_labels);
          setIsPending(true);
        }

        // Clear any existing polling before setting new one
        if (pollingRef.current) {
          clearTimeout(pollingRef.current);
        }
        // Continue polling
        pollingRef.current = setTimeout(() => {
          void fetchLabels();
        }, 5000);
      }
    } catch (error) {
      // Only set error if it's not a cancellation
      if (!axios.isCancel(error)) {
        console.error(error);
        setError("Failed to fetch unique labels");
        setIsPending(false);
      }
    }
  }, [accountId, filterId]);

  useEffect(() => {
    void fetchLabels();

    return () => {
      // Cleanup function to cancel both polling and ongoing request
      if (pollingRef.current) {
        clearTimeout(pollingRef.current);
      }
      if (cancelTokenSourceRef.current) {
        cancelTokenSourceRef.current.cancel("Component unmounted");
      }
    };
  }, [fetchLabels]);

  if (error) {
    return (
      <div className="w-[185px] h-8 flex items-center px-3 rounded-[8px] border-[1px] border-border-gray bg-white">
        <span className="text-[12px] text-red-500">Error loading options</span>
      </div>
    );
  }

  const formattedOptions = uniqueLabels.map((label) => ({
    key: label,
    label: label,
  }));

  return (
    <SelectDropdown
      multiSelectSaveLabel="Apply"
      multiSelectShowSaveButton
      multiSelectCommitOnChange
      options={formattedOptions.map((option) => ({
        key: option.key,
        label: option.label,
        value: option.key,
      }))}
      selected={isPending ? "Generating labels" : ""}
      onChange={() => {}}
      selectedValues={Array.isArray(condition.value) ? condition.value : []}
      onChangeMultiple={(newValues: string[]) => {
        setTempFilter((prev) => {
          if (!prev) return undefined;
          const newConditions = [...(prev.conditions || [])];
          newConditions[conditionIndex] = {
            ...newConditions[conditionIndex],
            value: newValues,
          };
          return { ...prev, conditions: newConditions };
        });
      }}
      showTooltip={true}
      multiSelect={true}
      placeholder="Select groups"
      className="w-[185px] h-[33px]"
      enableSearch={true}
      searchPlaceholder="Search"
      sideOffset={5}
      alignOffset={0}
      customTrigger={customTrigger}
    />
  );
};

type FiltersCustomTriggerProps = {
  active: boolean;
  size?: "sm" | "md";
  tooltipText?: string;
};

const FiltersCustomTrigger = ({
  active,
  size = "md",
  tooltipText = "Filters",
}: FiltersCustomTriggerProps) => {
  return (
    <SqTooltipProvider>
      <SqTooltip>
        <SqTooltipTrigger asChild>
          <SqButton
            size={size}
            variant={"outline"}
            hasOutline
            bgColor="white"
            hoverBgColor="#FBFBFB"
            shadow={"sm"}
            className="cursor-pointer"
          >
            <RiFilter3Line
              size={14}
              color={active ? "#FF4500" : "#9AA3BD"}
              className="shrink-0"
            />
            <span className="mt-[2px] text-[12px] text-brand-primary-800 font-medium">
              Add Filter
            </span>
          </SqButton>
        </SqTooltipTrigger>
        <SqTooltipContent side="bottom" align="start">
          {tooltipText}
        </SqTooltipContent>
      </SqTooltip>
    </SqTooltipProvider>
  );
};

export { FiltersCustomTrigger };
